import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  inventoryApi, siteApi, safetyKitApi,
  workerKitApi, workerSiteAssignmentApi,
  stationeryApi, itApi, workerApi, workerRequestApi, damageReportApi, materialUnitApi, repairTicketApi,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MaterialInfoPanel from '../../components/common/MaterialInfoPanel';
import SearchableSelect from '../../components/common/SearchableSelect';
import ExcelUploadMenu from '../../components/common/ExcelUploadMenu';
import { MATERIAL_TYPES, materialTypeLabel } from '../../constants/materialTypes';

// Local (not UTC) yyyy-mm-dd — used to default the Add Stock date picker to
// "today" without the off-by-one-day risk of Date#toISOString (which reads
// UTC and can show yesterday's date in timezones ahead of UTC, like IST,
// during the first hours after local midnight).
const todayLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgoLocalDate = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

// Date-range filter bar for the issuance/stock history modals — from/to
// pickers plus quick-select presets. Blank from+to means "all time"; the
// caller does the actual filtering against whatever date field it's showing.
const DateRangeFilter = ({ from, to, onFrom, onTo }) => {
  const today = todayLocalDate();
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-gray-100">
      <input type="date" className="input !py-1 !text-xs !w-auto" value={from} max={to || today} onChange={(e) => onFrom(e.target.value)} />
      <span className="text-xs text-gray-400">to</span>
      <input type="date" className="input !py-1 !text-xs !w-auto" value={to} min={from} max={today} onChange={(e) => onTo(e.target.value)} />
      {(from || to) && (
        <button className="text-xs text-indigo-600 hover:underline" onClick={() => { onFrom(''); onTo(''); }}>Clear</button>
      )}
      <div className="flex gap-1 ml-auto flex-wrap">
        {[['7D', 7], ['30D', 30], ['3M', 90], ['6M', 180], ['1Y', 365]].map(([l, n]) => (
          <button
            key={l}
            className="text-xs px-2 py-0.5 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600"
            onClick={() => { onFrom(daysAgoLocalDate(n)); onTo(today); }}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
};

// r[dateField] is an ISO date/datetime string — compares by date only.
const inDateRange = (row, dateField, from, to) => {
  if (!from && !to) return true;
  const d = (row[dateField] || '').slice(0, 10);
  if (!d) return false;
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
};

const Modal = ({ title, subtitle, children, onClose, wide = false, zClass = 'z-50' }) => (
  <div className={`fixed inset-0 ${zClass} flex items-center justify-center bg-black bg-opacity-40 p-4`}>
    <div className={`bg-white rounded-xl shadow-xl p-6 w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel, disabled = false }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary" disabled={disabled}>{submitLabel}</button>
  </div>
);

const SectionHeader = ({ title }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2 mt-5 first:mt-0">{title}</p>
);

// Material ID <select> + an inline "+ Add new Material ID" fallback for
// when the picker is empty (the exact deadlock a untracked-stock shortfall
// causes) — assigns one new code via materialUnitApi.create and selects it
// immediately, without leaving the Issue dialog. Shared by every Issue
// picker (Materials, Stationery, and the Issue-Request flow).
const MaterialIdPicker = ({ value, onChange, units, kitId, stationeryItemId, itItemId, siteId, onUnitAdded }) => {
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submitAdd = async () => {
    const code = newCode.trim();
    if (!code) return;
    setSaving(true);
    setError('');
    try {
      const res = await materialUnitApi.create({ kitId, stationeryItemId, itItemId, siteId, codes: [code] });
      const unit = res.data.data[0];
      onUnitAdded(unit);
      onChange(String(unit.id));
      setAdding(false);
      setNewCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add Material ID');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <label className="label">Material ID *</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">Select Material ID…</option>
        {units.map((u) => (
          <option key={u.id} value={u.id}>{u.materialIdCode}</option>
        ))}
      </select>
      {units.length === 0 && !adding && (
        <p className="text-xs text-amber-600 mt-1">No Material IDs currently in stock at this site for this item.</p>
      )}
      {adding ? (
        <div className="flex gap-2 mt-2">
          <input className="input flex-1 py-1" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="New Material ID" autoFocus />
          <button type="button" className="text-blue-600 hover:underline text-sm" disabled={saving || !newCode.trim()} onClick={submitAdd}>Add</button>
          <button type="button" className="text-gray-500 hover:underline text-sm" onClick={() => { setAdding(false); setError(''); }}>Cancel</button>
        </div>
      ) : (
        <button type="button" className="text-blue-600 hover:underline text-xs mt-1" onClick={() => setAdding(true)}>+ Add new Material ID</button>
      )}
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
};

// ── Worker detail panel — shows active issuances with optional Return buttons ─

const WorkerDetailPanel = ({
  kits, consumables, durables, loading, onReturnClick,
  pendingDamageWorkerKitIds, pendingDamageStationeryIssuanceIds, repairStatusByStationeryIssuanceId,
  onMarkRepairedClick, canRepair, onViewMaterialHistory,
  // IT — parallel to consumables/durables above, but a wholly separate item
  // pillar (see itApi). Left undefined for every caller that hasn't been
  // updated for IT (or for a user without it_view) so this panel renders
  // exactly as before in every existing (Materials/Stationery) call site.
  itConsumables, itDurables, repairStatusByItIssuanceId, canRepairIT,
}) => {
  const showIT = !!(itConsumables || itDurables);
  if (loading) return <div className="p-6 text-center text-sm text-gray-400">Loading…</div>;
  const total = kits.length + consumables.length + durables.length + (itConsumables?.length || 0) + (itDurables?.length || 0);
  if (total === 0) return (
    <div className="p-6 text-center text-sm text-gray-400">
      No active issuances for this worker at this site.
    </div>
  );
  return (
    <div className={`p-5 grid grid-cols-1 gap-5 ${showIT ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
      {/* Materials */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-3">
          Materials{' '}
          <span className="ml-1 bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-bold">{kits.length}</span>
        </p>
        {kits.length === 0 ? <p className="text-xs text-gray-400">None issued</p> : kits.map((k) => {
          const outstanding  = k.quantity - (k.returnedQuantity || 0);
          const isPaidItem   = (k._count?.salaryDeductions || 0) > 0;
          const isPendingDamage = pendingDamageWorkerKitIds?.has(k.id);
          return (
            <div key={k.id} className="mb-2 p-3 rounded-lg border border-indigo-100 bg-white">
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 truncate">{k.kit?.name}</p>
                    {isPaidItem && (
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200 whitespace-nowrap">
                        Paid Item
                      </span>
                    )}
                    {isPendingDamage && (
                      <span className="text-xs font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full border border-red-200 whitespace-nowrap">
                        Pending Damage Approval
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Qty: <strong>{outstanding}</strong> out · Issued {new Date(k.issuedDate).toLocaleDateString('en-GB')}
                  </p>
                  {k.materialUnit && (
                    <p className="text-xs mt-0.5">
                      Material ID:{' '}
                      {onViewMaterialHistory ? (
                        <button
                          className="font-mono text-indigo-600 hover:underline"
                          onClick={() => onViewMaterialHistory(k.materialUnit.id)}
                        >
                          {k.materialUnit.materialIdCode}
                        </button>
                      ) : <span className="font-mono text-gray-600">{k.materialUnit.materialIdCode}</span>}
                    </p>
                  )}
                  {k.validityEndsAt && (
                    <p className={`text-xs mt-0.5 ${new Date(k.validityEndsAt) > new Date() ? 'text-indigo-500' : 'text-orange-500'}`}>
                      {new Date(k.validityEndsAt) > new Date()
                        ? `Valid until ${new Date(k.validityEndsAt).toLocaleDateString('en-GB')}`
                        : `Validity expired`}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  {isPendingDamage ? null : (
                    onReturnClick && (
                      isPaidItem
                        ? <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded whitespace-nowrap">Paid – No Return</span>
                        : <button
                            className="text-xs text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded transition-colors"
                            onClick={() => onReturnClick({ kind: 'MATERIAL', target: k, outstanding })}
                          >
                            Return
                          </button>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Consumables */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-green-600 mb-3">
          Consumables{' '}
          <span className="ml-1 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">{consumables.length}</span>
        </p>
        {consumables.length === 0 ? <p className="text-xs text-gray-400">None issued</p> : consumables.map((i) => (
          <div key={i.id} className="mb-2 p-3 rounded-lg border border-green-100 bg-white">
            <p className="text-sm font-medium text-gray-900">{i.item?.name}</p>
            <p className="text-xs text-gray-400 mt-0.5">Qty: {i.quantity} · {new Date(i.issuedDate).toLocaleDateString('en-GB')}</p>
          </div>
        ))}
      </div>

      {/* Durables */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-purple-600 mb-3">
          Durables{' '}
          <span className="ml-1 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">{durables.length}</span>
        </p>
        {durables.length === 0 ? <p className="text-xs text-gray-400">None issued</p> : durables.map((i) => {
          const isPaidItem = (i._count?.deductions || 0) > 0;
          const isPendingDamage = pendingDamageStationeryIssuanceIds?.has(i.id);
          const repairStatus = repairStatusByStationeryIssuanceId?.get(i.id);
          const isInRepair = !!repairStatus;
          return (
            <div key={i.id} className="mb-2 p-3 rounded-lg border border-purple-100 bg-white">
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 truncate">{i.item?.name}</p>
                    {isPaidItem && (
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200 whitespace-nowrap">
                        Paid Item
                      </span>
                    )}
                    {isPendingDamage && (
                      <span className="text-xs font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full border border-red-200 whitespace-nowrap">
                        Pending Damage Approval
                      </span>
                    )}
                    {isInRepair && (
                      <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full border border-orange-200 whitespace-nowrap">
                        {repairStatus === 'UNDER_MAINTENANCE' ? 'Sent for Maintenance' : 'Needs Repair'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Issued {new Date(i.issuedDate).toLocaleDateString('en-GB')}</p>
                  {i.materialUnit && (
                    <p className="text-xs mt-0.5">
                      Material ID:{' '}
                      {onViewMaterialHistory ? (
                        <button
                          className="font-mono text-indigo-600 hover:underline"
                          onClick={() => onViewMaterialHistory(i.materialUnit.id)}
                        >
                          {i.materialUnit.materialIdCode}
                        </button>
                      ) : <span className="font-mono text-gray-600">{i.materialUnit.materialIdCode}</span>}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  {isPendingDamage || isInRepair ? null : (
                    isPaidItem
                      ? <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded whitespace-nowrap">Paid – No Return</span>
                      : <>
                          {onReturnClick && (
                            <button
                              className="text-xs text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded transition-colors"
                              onClick={() => onReturnClick({ kind: 'STATIONERY', target: i })}
                            >
                              Return
                            </button>
                          )}
                          {canRepair && onMarkRepairedClick && (
                            <button
                              className="text-xs text-white bg-orange-500 hover:bg-orange-600 px-2 py-1 rounded transition-colors"
                              onClick={() => onMarkRepairedClick({ kind: 'ISSUANCE', target: i })}
                            >
                              Mark as Needs Repair
                            </button>
                          )}
                        </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* IT — isolated pillar, only rendered when the caller (Workers tab)
          determined the acting user holds it_view; a user without it_view
          never receives itConsumables/itDurables at all, so this column
          simply doesn't exist for them (see showIT above). */}
      {showIT && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600 mb-3">
            IT{' '}
            <span className="ml-1 bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-bold">{(itConsumables?.length || 0) + (itDurables?.length || 0)}</span>
          </p>
          {(itConsumables?.length || 0) === 0 && (itDurables?.length || 0) === 0 ? (
            <p className="text-xs text-gray-400">None issued</p>
          ) : (
            <>
              {itConsumables?.map((i) => (
                <div key={`itc-${i.id}`} className="mb-2 p-3 rounded-lg border border-sky-100 bg-white">
                  <p className="text-sm font-medium text-gray-900">{i.item?.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Qty: {i.quantity} · {new Date(i.issuedDate).toLocaleDateString('en-GB')}</p>
                </div>
              ))}
              {itDurables?.map((i) => {
                const isPaidItem = (i._count?.deductions || 0) > 0;
                const repairStatus = repairStatusByItIssuanceId?.get(i.id);
                const isInRepair = !!repairStatus;
                return (
                  <div key={`itd-${i.id}`} className="mb-2 p-3 rounded-lg border border-sky-100 bg-white">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium text-gray-900 truncate">{i.item?.name}</p>
                          {isPaidItem && (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200 whitespace-nowrap">
                              Paid Item
                            </span>
                          )}
                          {isInRepair && (
                            <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full border border-orange-200 whitespace-nowrap">
                              {repairStatus === 'UNDER_MAINTENANCE' ? 'Sent for Maintenance' : 'Needs Repair'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">Issued {new Date(i.issuedDate).toLocaleDateString('en-GB')}</p>
                        {i.materialUnit && (
                          <p className="text-xs mt-0.5">
                            Material ID:{' '}
                            {onViewMaterialHistory ? (
                              <button
                                className="font-mono text-indigo-600 hover:underline"
                                onClick={() => onViewMaterialHistory(i.materialUnit.id)}
                              >
                                {i.materialUnit.materialIdCode}
                              </button>
                            ) : <span className="font-mono text-gray-600">{i.materialUnit.materialIdCode}</span>}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-1.5">
                        {isInRepair ? null : (
                          isPaidItem
                            ? <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded whitespace-nowrap">Paid – No Return</span>
                            : <>
                                {onReturnClick && (
                                  <button
                                    className="text-xs text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded transition-colors"
                                    onClick={() => onReturnClick({ kind: 'IT', target: i })}
                                  >
                                    Return
                                  </button>
                                )}
                                {canRepairIT && onMarkRepairedClick && (
                                  <button
                                    className="text-xs text-white bg-orange-500 hover:bg-orange-600 px-2 py-1 rounded transition-colors"
                                    onClick={() => onMarkRepairedClick({ kind: 'ISSUANCE', target: i, itemType: 'IT' })}
                                  >
                                    Mark as Needs Repair
                                  </button>
                                )}
                              </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const { user, hasPermission, isSiteRestricted: isStorekeeper } = useAuth();
  const location = useLocation();

  // IT is a deliberately isolated third item pillar (see itApi) — holding
  // inventory_view/stationery_manage/etc. grants NOTHING here. The IT tab
  // itself, and every IT read (listing/history), is gated on this alone.
  const canViewIT = hasPermission('it_view');
  // Materials and Stationery mirror the same isolation model as IT — each
  // pillar has its own least-privilege "view" permission, and holding a
  // write permission for one pillar (e.g. it_issue) grants nothing in the
  // others. Previously Materials/Stationery tabs were shown unconditionally
  // regardless of permission, which is exactly what let an IT-only group see
  // tabs with nothing granted for them.
  const canViewMaterials = hasPermission('inventory_view');
  const canViewStationery = hasPermission('stationery_view');

  const [sites, setSites]                   = useState([]);
  // user.siteId is Portal's own legacy single-site field — it's never
  // updated by MaterialWatch's own (multi-site-capable) WorkerSiteAssignment
  // flow, so a storekeeper assigned only through that flow has it stuck at
  // null. user.siteIds is what authenticateLocal.js actually computes from
  // WorkerSiteAssignment and is always current; prefer it, falling back to
  // the legacy field only if it's somehow the sole one populated.
  const [selectedSiteId, setSelectedSiteId] = useState(isStorekeeper ? (user.siteIds?.[0] ?? user.siteId) : null);
  const [activeTab, setActiveTab]           = useState(() => {
    if (hasPermission('inventory_view')) return 'materials';
    if (hasPermission('stationery_view')) return 'stationery';
    if (hasPermission('it_view')) return 'it';
    return 'workers';
  });

  // ── Auto-open worker from navigation state (redirect from delete flow) ───────
  const [pendingExpandWorkerId, setPendingExpandWorkerId] = useState(
    location.state?.autoOpenWorkerId || null
  );
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');

  // ── Materials data ──────────────────────────────────────────────────────────
  const [inventory, setInventory]           = useState([]);
  const [allKits, setAllKits]               = useState([]);
  const [siteWorkerKits, setSiteWorkerKits] = useState([]);
  // Every active kit held by a worker assigned to this site, regardless of
  // WHICH site actually issued it — e.g. a Guest here who already holds
  // materials issued at their home site. Site's own stock accounting
  // (issuedByKit, Stock Levels tables) must stay scoped to siteWorkerKits
  // above; this is only for the Workers tab's per-worker item counts, so a
  // storekeeper managing a guest/transferred worker isn't shown a false 0.
  const [workerHoldingsKits, setWorkerHoldingsKits] = useState([]);

  // ── Stationery data ─────────────────────────────────────────────────────────
  const [statInventory, setStatInventory]             = useState([]);
  const [allStatItems, setAllStatItems]               = useState([]);
  const [statIssuances, setStatIssuances]             = useState([]);
  // Same cross-site rationale as workerHoldingsKits above, for stationery.
  const [workerHoldingsIssuances, setWorkerHoldingsIssuances] = useState([]);
  // Open Repair & Maintenance tickets (PENDING_REPAIR/UNDER_MAINTENANCE) for
  // Stationery Durables — replaces the old dead repairStatus:'UNDER_REPAIR'
  // issuance query with the new RepairTicket-based pipeline.
  const [repairTickets, setRepairTickets]             = useState([]);
  const [statCategoryFilter, setStatCategoryFilter]   = useState('CONSUMABLE');
  const [matTypeFilter, setMatTypeFilter]             = useState(
    () => new URLSearchParams(location.search).get('type') || 'ALL'
  );

  // ── IT data ──────────────────────────────────────────────────────────────────
  // Parallel to the Stationery data above, but a wholly separate item pillar
  // (itApi) — every fetch below is gated by canViewIT so a user without
  // it_view never even issues these requests (see loadSiteData).
  const [itInventory, setItInventory]                 = useState([]);
  const [allItItems, setAllItItems]                   = useState([]);
  const [itIssuances, setItIssuances]                 = useState([]);
  const [workerHoldingsItIssuances, setWorkerHoldingsItIssuances] = useState([]);
  // IT has no Consumable/Durable split (every IT item behaves like a
  // Durable) — fixed at 'DURABLE', never toggled by the user.
  const [itCategoryFilter]       = useState('DURABLE');

  // Sidebar submenu navigation (/inventory?type=X) while already on this page
  // doesn't remount the component, so react to search-string changes directly.
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('type');
    if (t) { setActiveTab('materials'); setMatTypeFilter(t); }
  }, [location.search]);

  // ── Workers tab data ────────────────────────────────────────────────────────
  const [siteAssignments, setSiteAssignments]   = useState([]);
  const [expandedWorkerId, setExpandedWorkerId] = useState(null);
  const [workerKitDetail, setWorkerKitDetail]   = useState([]);
  const [workerStatDetail, setWorkerStatDetail] = useState([]);
  const [workerItDetail, setWorkerItDetail]     = useState([]);
  const [loadingDetail, setLoadingDetail]       = useState(false);

  // ── Workers tab: global search + assign ─────────────────────────────────────
  const [workerSearch, setWorkerSearch]           = useState('');
  const [allWorkers, setAllWorkers]               = useState([]);
  const [allWorkersLoaded, setAllWorkersLoaded]   = useState(false);
  const [allWorkersLoading, setAllWorkersLoading] = useState(false);
  const [assignTarget, setAssignTarget]           = useState(null);
  const [assignSiteId, setAssignSiteId]           = useState('');
  const [assignError, setAssignError]             = useState('');

  // ── Add Guest ────────────────────────────────────────────────────────────────
  const [showAddGuest, setShowAddGuest]   = useState(false);
  const [addGuestForm, setAddGuestForm]   = useState({ name: '', employeeId: '', role: '' });
  const [addGuestError, setAddGuestError] = useState('');
  const [addGuestSaving, setAddGuestSaving] = useState(false);
  // If the typed Employee ID already belongs to someone, we reuse that
  // Worker (a new site assignment tagged Guest) instead of creating a
  // duplicate record — this holds the found worker, or null.
  const [addGuestFoundWorker, setAddGuestFoundWorker] = useState(null);
  const [addGuestChecking, setAddGuestChecking]       = useState(false);
  // Live Employee ID/name suggestions as the storekeeper types — separate
  // from the exact-match blur lookup above, which still runs once a
  // suggestion is picked (or the field is left as free text) to resolve the
  // full worker record.
  const [addGuestSuggestions, setAddGuestSuggestions] = useState([]);
  const [addGuestShowSuggestions, setAddGuestShowSuggestions] = useState(false);

  // ── Requests tab ─────────────────────────────────────────────────────────────
  const [requests, setRequests]                 = useState([]);
  const [requestsLoading, setRequestsLoading]     = useState(false);
  const [requestsLoaded, setRequestsLoaded]       = useState(false);
  const [requestsKindFilter, setRequestsKindFilter] = useState('ALL'); // 'ALL' | 'MATERIAL' | 'STATIONERY' | 'IT'
  const [replyTarget, setReplyTarget]           = useState(null); // the request row being replied to
  const [replyMessage, setReplyMessage]         = useState('');
  const [replyError, setReplyError]             = useState('');
  const [replySaving, setReplySaving]           = useState(false);
  const [closeTarget, setCloseTarget]           = useState(null); // the request row being closed
  const [closeMessage, setCloseMessage]         = useState('');
  const [closeError, setCloseError]             = useState('');
  const [closeSaving, setCloseSaving]           = useState(false);
  const [issueRequestTarget, setIssueRequestTarget] = useState(null);
  const [issueRequestInventoryId, setIssueRequestInventoryId] = useState('');
  const [issueRequestError, setIssueRequestError] = useState('');
  const [issueRequestSaving, setIssueRequestSaving] = useState(false);
  // Only relevant when the requested kit/item requires a Material ID —
  // fetched once a batch (materials) or the target site (stationery) is
  // known, same pattern as the main Issue form's requiresMaterialId picker.
  const [issueRequestMaterialUnits, setIssueRequestMaterialUnits] = useState([]);
  const [issueRequestMaterialUnitId, setIssueRequestMaterialUnitId] = useState('');
  // Same still-valid-reissue / max-quantity confirmation flow as the main
  // Issue form — without these, a request for an item the worker already
  // validly holds (or already holds up to the configured max) could never
  // be fulfilled from here at all.
  const [issueRequestValidityWarn, setIssueRequestValidityWarn] = useState(null);
  const [issueRequestConfirmDeduction, setIssueRequestConfirmDeduction] = useState(false);
  const [issueRequestMaxQtyWarn, setIssueRequestMaxQtyWarn] = useState(null);
  const [issueRequestConfirmMaxQtyDeduction, setIssueRequestConfirmMaxQtyDeduction] = useState(false);

  // ── Kit detail modal (issuance HISTORY for a material at this site) —
  // read-only. Fetched on demand (kitId+siteId, no activeOnly filter) rather
  // than reusing the site's already-loaded active-only list, since this
  // shows every issuance ever made from this site's stock, returned or not.
  const [showKitDetail, setShowKitDetail]           = useState(false);
  const [kitDetailTarget, setKitDetailTarget]       = useState(null);
  const [kitDetailIssuances, setKitDetailIssuances] = useState([]);
  const [kitDetailLoading, setKitDetailLoading]     = useState(false);
  const [kitDetailFrom, setKitDetailFrom]           = useState('');
  const [kitDetailTo, setKitDetailTo]               = useState('');

  // ── Stat (stationery) detail modal — same idea, for a stationery item ──────
  const [showStatDetail, setShowStatDetail]           = useState(false);
  const [statDetailTarget, setStatDetailTarget]       = useState(null);
  const [statDetailIssuances, setStatDetailIssuances] = useState([]);
  const [statDetailLoading, setStatDetailLoading]     = useState(false);
  const [statDetailFrom, setStatDetailFrom]           = useState('');
  const [statDetailTo, setStatDetailTo]               = useState('');

  // ── IT detail modal — same idea, for an IT item ────────────────────────────
  const [showItDetail, setShowItDetail]           = useState(false);
  const [itDetailTarget, setItDetailTarget]       = useState(null);
  const [itDetailIssuances, setItDetailIssuances] = useState([]);
  const [itDetailLoading, setItDetailLoading]     = useState(false);
  const [itDetailFrom, setItDetailFrom]           = useState('');
  const [itDetailTo, setItDetailTo]               = useState('');

  // ── Stock history modal (how "Stock Available" got here) — shared by
  // materials and stationery, distinguished by `kind`.
  const [showStockHistory, setShowStockHistory]     = useState(false);
  const [stockHistoryTarget, setStockHistoryTarget] = useState(null); // { kind: 'MATERIAL'|'STATIONERY', name }
  const [stockHistoryRows, setStockHistoryRows]     = useState([]);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockHistoryFrom, setStockHistoryFrom]     = useState('');
  const [stockHistoryTo, setStockHistoryTo]         = useState('');

  const openKitIssuedHistory = async (r) => {
    setKitDetailTarget(r); setKitDetailIssuances([]); setError(''); setShowKitDetail(true); setKitDetailLoading(true);
    setKitDetailFrom(''); setKitDetailTo('');
    try {
      const res = await workerKitApi.list({ kitId: r.kitId, siteId: selectedSiteId });
      setKitDetailIssuances(res.data.data || []);
    } catch { setKitDetailIssuances([]); } finally { setKitDetailLoading(false); }
  };

  const openStatIssuedHistory = async (sinv) => {
    setStatDetailTarget(sinv); setStatDetailIssuances([]); setError(''); setShowStatDetail(true); setStatDetailLoading(true);
    setStatDetailFrom(''); setStatDetailTo('');
    try {
      const res = await stationeryApi.listIssuances({ itemId: sinv.itemId, siteId: selectedSiteId });
      setStatDetailIssuances(res.data.data || []);
    } catch { setStatDetailIssuances([]); } finally { setStatDetailLoading(false); }
  };

  const openItIssuedHistory = async (iinv) => {
    setItDetailTarget(iinv); setItDetailIssuances([]); setError(''); setShowItDetail(true); setItDetailLoading(true);
    setItDetailFrom(''); setItDetailTo('');
    try {
      const res = await itApi.listIssuances({ itemId: iinv.itemId, siteId: selectedSiteId });
      setItDetailIssuances(res.data.data || []);
    } catch { setItDetailIssuances([]); } finally { setItDetailLoading(false); }
  };

  const openStockHistory = async (kind, target) => {
    setStockHistoryTarget({ kind, name: kind === 'MATERIAL' ? target.kit?.name : target.item?.name });
    setStockHistoryRows([]); setError(''); setShowStockHistory(true); setStockHistoryLoading(true);
    setStockHistoryFrom(''); setStockHistoryTo('');
    try {
      const res = kind === 'MATERIAL'
        ? await inventoryApi.stockHistory({ siteId: selectedSiteId, kitId: target.kitId })
        : kind === 'IT'
        ? await itApi.stockHistory({ siteId: selectedSiteId, itemId: target.itemId })
        : await stationeryApi.stockHistory({ siteId: selectedSiteId, itemId: target.itemId });
      setStockHistoryRows(res.data.data || []);
    } catch { setStockHistoryRows([]); } finally { setStockHistoryLoading(false); }
  };

  // A worker whose row we're showing under THIS site's issuance history may
  // no longer be assigned here (they issued it here, then transferred away —
  // the issuance stays put, see workerSiteAssignments.js's transfer()).
  const isWorkerOfThisSite = (workerId) => siteAssignments.some((a) => a.workerId === workerId);

  // ── Add / Edit stock (materials) ────────────────────────────────────────────
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStockForm, setAddStockForm] = useState({ kitId: '', quantity: '', receivedDate: todayLocalDate(), remarks: '' });
  // One Material ID per line — covers both manual entry and pasting a
  // column copied from Excel, without a separate file-upload endpoint.
  const [addStockMaterialIds, setAddStockMaterialIds] = useState('');
  const [editInvId, setEditInvId]       = useState(null);
  // The damaged quantity of the row being edited — the Edit Stock quantity
  // field shows/edits "In Stock" (quantity - damagedQuantity), matching what
  // the Materials table displays, not the raw StoreInventory.quantity (which
  // still includes damaged units). This is added back on submit so the
  // saved raw quantity keeps damagedQuantity accounting consistent with the
  // rest of the app.
  const [editInvDamagedQty, setEditInvDamagedQty] = useState(0);

  // ── Add stock (stationery) ───────────────────────────────────────────────────
  const [showAddStatStock, setShowAddStatStock]   = useState(false);
  const [addStatStockForm, setAddStatStockForm]   = useState({ itemId: '', quantity: '' });
  const [addStatStockError, setAddStatStockError] = useState('');

  // ── Add stock (IT) ────────────────────────────────────────────────────────────
  const [showAddItStock, setShowAddItStock]   = useState(false);
  const [addItStockForm, setAddItStockForm]   = useState({ itemId: '', quantity: '' });
  const [addItStockError, setAddItStockError] = useState('');

  // ── Bulk stock upload (Materials / Stationery) ──────────────────────────────
  const materialsBulkFileRef  = useRef(null);
  const stationeryBulkFileRef = useRef(null);
  const [bulkStockLoading, setBulkStockLoading] = useState(false);
  const [bulkStockPending, setBulkStockPending] = useState(null); // { kind, file, willUpdate, skipped, failed, failedRecords, total } — awaiting confirm/cancel
  const [bulkStockResult, setBulkStockResult]   = useState(null); // { kind: 'materials'|'stationery', total, updated, skipped, failed, failedRecords }
  const [bulkStockError, setBulkStockError]     = useState('');

  // ── Issue modal (Workers tab) ───────────────────────────────────────────────
  const [issueWorker, setIssueWorker]           = useState(null);
  const [issueType, setIssueType]               = useState('material');
  // Material issue
  const [issueMatType, setIssueMatType]         = useState('ALL');
  const [selectedInv, setSelectedInv]           = useState(null);
  const [matQty, setMatQty]                     = useState(1);
  const [matRemarks, setMatRemarks]             = useState('');
  const [availableMaterialUnits, setAvailableMaterialUnits] = useState([]);
  const [selectedMaterialUnitId, setSelectedMaterialUnitId] = useState('');
  const [validityWarn, setValidityWarn]         = useState(null);
  const [confirmDeduction, setConfirmDeduction] = useState(false);
  // Independent of validityWarn/confirmDeduction — a single issue can trip
  // both the re-issue-within-validity warning and the max-quantity warning
  // at once, each needing its own confirmation.
  const [maxQtyWarn, setMaxQtyWarn]                 = useState(null);
  const [confirmMaxQtyDeduction, setConfirmMaxQtyDeduction] = useState(false);
  // Stationery issue
  const [statCategory, setStatCategory]         = useState('CONSUMABLE');
  const [statIssueItemId, setStatIssueItemId]   = useState('');
  const [statIssueQty, setStatIssueQty]         = useState(1);
  const [statIssueRemarks, setStatIssueRemarks] = useState('');
  const [statIssueWarn, setStatIssueWarn]       = useState(null);
  const [statConfirmDed, setStatConfirmDed]     = useState(false);
  const [statMaxQtyWarn, setStatMaxQtyWarn]                 = useState(null);
  const [statConfirmMaxQtyDed, setStatConfirmMaxQtyDed]     = useState(false);
  const [availableStatMaterialUnits, setAvailableStatMaterialUnits] = useState([]);
  const [selectedStatMaterialUnitId, setSelectedStatMaterialUnitId] = useState('');
  // IT issue — parallel to Stationery issue above
  // Same fixed-'DURABLE' convention as itCategoryFilter above.
  const [itCategory]             = useState('DURABLE');
  const [itIssueItemId, setItIssueItemId]       = useState('');
  const [itIssueQty, setItIssueQty]             = useState(1);
  const [itIssueRemarks, setItIssueRemarks]     = useState('');
  const [itIssueWarn, setItIssueWarn]           = useState(null);
  const [itConfirmDed, setItConfirmDed]         = useState(false);
  const [itMaxQtyWarn, setItMaxQtyWarn]                 = useState(null);
  const [itConfirmMaxQtyDed, setItConfirmMaxQtyDed]     = useState(false);
  const [availableItMaterialUnits, setAvailableItMaterialUnits] = useState([]);
  const [selectedItMaterialUnitId, setSelectedItMaterialUnitId] = useState('');

  // ── Return chooser — Normal Return vs Damaged Return ────────────────────────
  const [returnChoiceTarget, setReturnChoiceTarget] = useState(null); // { kind: 'MATERIAL'|'STATIONERY'|'IT', target, outstanding? }

  // ── Return — Material ───────────────────────────────────────────────────────
  const [matReturnTarget, setMatReturnTarget] = useState(null);
  const [matReturnForm, setMatReturnForm]     = useState({ returnedQuantity: 1 });
  // One Material ID per returned unit — only needed when this issuance
  // predates requiresMaterialId tracking (no matReturnTarget.materialUnit)
  // but the kit now requires one.
  const [matReturnMaterialIds, setMatReturnMaterialIds] = useState('');

  // ── Return — Stationery ─────────────────────────────────────────────────────
  const [statReturnTarget, setStatReturnTarget] = useState(null);
  const [statReturnForm, setStatReturnForm]     = useState({ returnedQty: 1 });
  const [statReturnMaterialIds, setStatReturnMaterialIds] = useState('');

  // ── Return — IT ──────────────────────────────────────────────────────────────
  const [itReturnTarget, setItReturnTarget] = useState(null);
  const [itReturnForm, setItReturnForm]     = useState({ returnedQty: 1 });
  const [itReturnMaterialIds, setItReturnMaterialIds] = useState('');

  // ── Flag Damaged (per-item damage report, pending approval) ────────────────
  const [damageFlagTarget, setDamageFlagTarget]   = useState(null); // { kind: 'MATERIAL'|'STATIONERY'|'INVENTORY', target }
  const [damageFlagRemarks, setDamageFlagRemarks] = useState('');
  const [damageFlagFiles, setDamageFlagFiles]     = useState([]);
  const [damageFlagError, setDamageFlagError]     = useState('');
  const [damageFlagSaving, setDamageFlagSaving]   = useState(false);
  // Only used when kind === 'INVENTORY' — a StoreInventory batch is many
  // units, so (unlike a worker-held item) the flagger must say how many.
  const [damageFlagQuantity, setDamageFlagQuantity]           = useState(1);
  const [damageFlagAvailableUnits, setDamageFlagAvailableUnits] = useState([]);
  const [damageFlagUnitIds, setDamageFlagUnitIds]             = useState([]);

  // ── Pending Damage Approval tab ──────────────────────────────────────────────
  const [damageReports, setDamageReports]             = useState([]);
  const [damageReportsLoading, setDamageReportsLoading] = useState(false);
  const [damageReportsLoaded, setDamageReportsLoaded]   = useState(false);
  const [rejectTarget, setRejectTarget]                 = useState(null);
  const [rejectRemarks, setRejectRemarks]               = useState('');
  const [rejectError, setRejectError]                   = useState('');
  // Own-flagged-items-pending-approval visibility on the Workers tab — every
  // damage_flagging holder needs to see their own flag is still pending, not
  // just damage_approval holders reviewing the queue. Separate from the
  // damage-approval-tab-gated `damageReports` state above (which only loads
  // for damage_approval holders and only when that tab is opened).
  const [pendingDamageWorkerKitIds, setPendingDamageWorkerKitIds] = useState(new Set());
  const [pendingDamageStationeryIssuanceIds, setPendingDamageStationeryIssuanceIds] = useState(new Set());
  const [pendingDamageLoaded, setPendingDamageLoaded] = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const loadSiteData = async (siteId) => {
    if (!siteId) return;
    // repairTicketApi.list is gated server-side behind repair_management —
    // only fetch it when the acting user actually holds that permission, or
    // Promise.all rejects on the 403 and the whole page (including the
    // inventory/stationery/worker data below, all of which loaded fine)
    // fails to render, showing a raw "Permission required" error instead.
    const [invRes, kitsRes, wkRes, stInvRes, stAllItemsRes, stIssRes, repairRes, assignRes, itInvRes, itAllItemsRes, itIssRes] = await Promise.all([
      // Materials stock — gated server-side by inventory_view; never issued
      // at all otherwise, same "not just hidden client-side" rule as IT below.
      canViewMaterials ? inventoryApi.list({ siteId }) : Promise.resolve({ data: { data: [] } }),
      safetyKitApi.list(),
      workerKitApi.list({ siteId, activeOnly: 'true' }),
      // Stationery stock/issuances — gated server-side by stationery_view;
      // the item-master catalog (stationeryApi.listItems, below) stays open
      // to any authenticated user regardless, same split as Materials'
      // open safetyKitApi.list() vs gated inventoryApi.list() above.
      canViewStationery ? stationeryApi.listInventory({ siteId }) : Promise.resolve({ data: { data: [] } }),
      stationeryApi.listItems({ isActive: true }),
      canViewStationery ? stationeryApi.listIssuances({ siteId, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
      // repairTicketApi.list is gated server-side behind repair_management OR
      // it_repair_management, and returns whichever side(s) the caller can
      // see (see repairTickets.js list()) — fetch it whenever either is held.
      (hasPermission('repair_management') || hasPermission('it_repair_management'))
        ? repairTicketApi.list({ siteId }) : Promise.resolve({ data: { data: [] } }),
      workerSiteAssignmentApi.list({ siteId }),
      // IT — fetched only when it_view is held; never issued at all otherwise
      // (not just hidden client-side), so an unauthorized user never even
      // sees an IT 403 in the network tab, let alone IT data.
      canViewIT ? itApi.listInventory({ siteId }) : Promise.resolve({ data: { data: [] } }),
      canViewIT ? itApi.listItems({ isActive: true }) : Promise.resolve({ data: { data: [] } }),
      canViewIT ? itApi.listIssuances({ siteId, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
    ]);
    setInventory(invRes.data.data);
    setAllKits(kitsRes.data.data);
    setSiteWorkerKits(wkRes.data.data);
    setStatInventory(stInvRes.data.data);
    setAllStatItems(stAllItemsRes.data.data);
    setStatIssuances(stIssRes.data.data);
    // All tickets (every status) — the pipeline table only shows the open
    // ones (derived below), but Needs Repair / Scrapped detail views need
    // the full history for this site.
    setRepairTickets(repairRes.data.data || []);
    setSiteAssignments(assignRes.data.data);
    setItInventory(itInvRes.data.data);
    setAllItItems(itAllItemsRes.data.data);
    setItIssuances(itIssRes.data.data);

    // Cross-site holdings for the workers assigned/guest here — see
    // workerHoldingsKits/workerHoldingsIssuances declarations for why this
    // is a separate, unscoped fetch instead of reusing wkRes/stIssRes above.
    const workerIds = [...new Set(assignRes.data.data.map((a) => a.workerId))];
    if (workerIds.length > 0) {
      const [holdKitsRes, holdIssRes, holdItIssRes] = await Promise.all([
        workerKitApi.list({ workerIds: workerIds.join(','), activeOnly: 'true' }),
        canViewStationery ? stationeryApi.listIssuances({ workerIds: workerIds.join(','), isReturned: 'false' }) : Promise.resolve({ data: { data: [] } }),
        canViewIT ? itApi.listIssuances({ workerIds: workerIds.join(','), isReturned: 'false' }) : Promise.resolve({ data: { data: [] } }),
      ]);
      setWorkerHoldingsKits(holdKitsRes.data.data);
      setWorkerHoldingsIssuances(holdIssRes.data.data);
      setWorkerHoldingsItIssuances(holdItIssRes.data.data);
    } else {
      setWorkerHoldingsKits([]);
      setWorkerHoldingsIssuances([]);
      setWorkerHoldingsItIssuances([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');
      try {
        const siteRes = await siteApi.list();
        const siteList = siteRes.data.data;
        setSites(siteList);
        const preferredSiteId = location.state?.autoSiteId;
        const initId = isStorekeeper
          ? (user.siteIds?.[0] ?? user.siteId)
          : (preferredSiteId && siteList.some(s => s.id === preferredSiteId) ? preferredSiteId : siteList[0]?.id || null);
        if (initId) {
          setSelectedSiteId(initId);
          await loadSiteData(initId);
        }
        // If redirected from delete flow, switch to workers tab
        if (location.state?.autoOpenWorkerId) {
          setActiveTab('workers');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleSiteChange = async (siteId) => {
    const id = parseInt(siteId);
    setSelectedSiteId(id);
    setExpandedWorkerId(null);
    setError('');
    setLoading(true);
    try { await loadSiteData(id); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load site data'); }
    finally { setLoading(false); }
  };

  const refresh = async () => { if (selectedSiteId) await loadSiteData(selectedSiteId); };

  // ── Bulk stock upload (Materials / Stationery) ──────────────────────────────
  const downloadBlob = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleMaterialsBulkTemplate = async () => {
    setBulkStockError('');
    try {
      const res = await inventoryApi.bulkStockTemplate(selectedSiteId);
      downloadBlob(res.data, `materials_stock_template_${selectedSite?.name || 'site'}.xlsx`);
    } catch (err) { setBulkStockError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleStationeryBulkTemplate = async () => {
    setBulkStockError('');
    try {
      const res = await stationeryApi.bulkStockTemplate(selectedSiteId);
      downloadBlob(res.data, `stationery_stock_template_${selectedSite?.name || 'site'}.xlsx`);
    } catch (err) { setBulkStockError(err.response?.data?.message || 'Failed to download template'); }
  };

  // Two-step upload: dry-run the file first (parses/validates, writes
  // nothing) to find out how many rows would actually change something, then
  // ask the person to confirm before the real, committing request. If
  // nothing would be updated (everything skipped or failed), there's nothing
  // to confirm — go straight to the results modal so they see why.
  const runBulkStockDryRun = async (kind, file) => {
    setBulkStockLoading(true); setBulkStockError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const api2 = kind === 'materials' ? inventoryApi : stationeryApi;
      const res = await api2.bulkStockUpload(selectedSiteId, formData, true);
      const preview = res.data.data;
      if (preview.willUpdate > 0) {
        setBulkStockPending({ kind, file, ...preview });
      } else {
        setBulkStockResult({ kind, updated: 0, skipped: preview.skipped, failed: preview.failed, failedRecords: preview.failedRecords, total: preview.total });
      }
    } catch (err) { setBulkStockError(err.response?.data?.message || 'Bulk stock upload failed'); }
    finally { setBulkStockLoading(false); }
  };

  const handleMaterialsBulkUploadChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) runBulkStockDryRun('materials', file);
  };

  const handleStationeryBulkUploadChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) runBulkStockDryRun('stationery', file);
  };

  const confirmBulkStockUpload = async () => {
    const { kind, file } = bulkStockPending;
    setBulkStockPending(null);
    setBulkStockLoading(true); setBulkStockError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const api2 = kind === 'materials' ? inventoryApi : stationeryApi;
      const res = await api2.bulkStockUpload(selectedSiteId, formData);
      setBulkStockResult({ kind, ...res.data.data });
      await refresh();
    } catch (err) { setBulkStockError(err.response?.data?.message || 'Bulk stock upload failed'); }
    finally { setBulkStockLoading(false); }
  };

  // ── After issue/return: refresh site data + expanded worker detail ──────────
  const afterAction = async (workerId) => {
    await refresh();
    if (expandedWorkerId) {
      const wid = workerId ?? expandedWorkerId;
      if (wid === expandedWorkerId) {
        setLoadingDetail(true);
        try {
          const [kitsRes, issRes, itIssRes] = await Promise.all([
            workerKitApi.list({ workerId: expandedWorkerId, activeOnly: 'true' }),
            canViewStationery ? stationeryApi.listIssuances({ workerId: expandedWorkerId, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
            canViewIT ? itApi.listIssuances({ workerId: expandedWorkerId, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
          ]);
          setWorkerKitDetail(kitsRes.data.data);
          setWorkerStatDetail(issRes.data.data);
          setWorkerItDetail(itIssRes.data.data);
        } catch { setWorkerKitDetail([]); setWorkerStatDetail([]); setWorkerItDetail([]); }
        setLoadingDetail(false);
      }
    }
  };

  // ── Workers tab: expand / collapse ─────────────────────────────────────────
  const handleExpandWorker = async (worker) => {
    if (expandedWorkerId === worker.id) { setExpandedWorkerId(null); return; }
    setExpandedWorkerId(worker.id);
    setLoadingDetail(true);
    try {
      const [kitsRes, issRes, itIssRes] = await Promise.all([
        workerKitApi.list({ workerId: worker.id, activeOnly: 'true' }),
        canViewStationery ? stationeryApi.listIssuances({ workerId: worker.id, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
        canViewIT ? itApi.listIssuances({ workerId: worker.id, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
      ]);
      setWorkerKitDetail(kitsRes.data.data);
      setWorkerStatDetail(issRes.data.data);
      setWorkerItDetail(itIssRes.data.data);
    } catch { setWorkerKitDetail([]); setWorkerStatDetail([]); setWorkerItDetail([]); }
    setLoadingDetail(false);
  };

  // ── Auto-expand worker once assignments load (from navigation redirect) ──────
  useEffect(() => {
    if (!pendingExpandWorkerId || siteAssignments.length === 0) return;
    const workerList = [...new Map(siteAssignments.map(a => [a.workerId, a.worker])).values()].filter(Boolean);
    const target = workerList.find(w => w.id === pendingExpandWorkerId);
    if (target) {
      handleExpandWorker(target);
      setPendingExpandWorkerId(null);
    }
  }, [siteAssignments, pendingExpandWorkerId]); // eslint-disable-line

  // ── Load all workers when Workers tab is first opened ───────────────────────
  useEffect(() => {
    if (activeTab !== 'workers' || allWorkersLoaded) return;
    setAllWorkersLoading(true);
    workerApi.list()
      .then((res) => { setAllWorkers(res.data.data); setAllWorkersLoaded(true); })
      .catch(() => {})
      .finally(() => setAllWorkersLoading(false));
  }, [activeTab, allWorkersLoaded]); // eslint-disable-line

  // ── Load requests when the Requests tab is first opened ─────────────────────
  const loadRequests = () => {
    setRequestsLoading(true);
    workerRequestApi.list()
      .then((res) => { setRequests(res.data.data || []); setRequestsLoaded(true); })
      .catch(() => {})
      .finally(() => setRequestsLoading(false));
  };
  useEffect(() => {
    if (activeTab !== 'requests' || requestsLoaded) return;
    loadRequests();
  }, [activeTab, requestsLoaded]); // eslint-disable-line

  const openReply = (request) => {
    setReplyTarget(request);
    setReplyMessage(request.replyMessage || '');
    setReplyError('');
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    setReplySaving(true);
    setReplyError('');
    try {
      const res = await workerRequestApi.reply(replyTarget.id, { message: replyMessage });
      setRequests((prev) => prev.map((r) => (r.id === replyTarget.id ? res.data.data : r)));
      setReplyTarget(null);
    } catch (err) {
      setReplyError(err.response?.data?.message || 'Error sending reply');
    } finally {
      setReplySaving(false);
    }
  };

  const openClose = (request) => {
    setCloseTarget(request);
    setCloseMessage('');
    setCloseError('');
  };

  const handleCloseSubmit = async (e) => {
    e.preventDefault();
    setCloseSaving(true);
    setCloseError('');
    try {
      const res = await workerRequestApi.close(closeTarget.id, { message: closeMessage || undefined });
      setRequests((prev) => prev.map((r) => (r.id === closeTarget.id ? res.data.data : r)));
      setCloseTarget(null);
    } catch (err) {
      setCloseError(err.response?.data?.message || 'Error closing request');
    } finally {
      setCloseSaving(false);
    }
  };

  // ── Issue directly from a pending request ───────────────────────────────────
  const openIssueRequest = (wr) => {
    setIssueRequestTarget(wr);
    setIssueRequestInventoryId('');
    setIssueRequestError('');
    setIssueRequestMaterialUnits([]);
    setIssueRequestMaterialUnitId('');
    setIssueRequestValidityWarn(null);
    setIssueRequestConfirmDeduction(false);
    setIssueRequestMaxQtyWarn(null);
    setIssueRequestConfirmMaxQtyDeduction(false);
    // Stationery/IT items requiring a Material ID are issued straight from
    // the selected site's stock (no batch picker) — fetch units immediately.
    if (wr.itemKind === 'STATIONERY' && wr.stationeryItem?.requiresMaterialId && selectedSiteId) {
      materialUnitApi.list({ stationeryItemId: wr.stationeryItemId, siteId: selectedSiteId, status: 'IN_STOCK' })
        .then((res) => setIssueRequestMaterialUnits(res.data.data || []))
        .catch(() => {});
    } else if (wr.itemKind === 'IT' && wr.itItem?.requiresMaterialId && selectedSiteId) {
      materialUnitApi.list({ itItemId: wr.itItemId, siteId: selectedSiteId, status: 'IN_STOCK' })
        .then((res) => setIssueRequestMaterialUnits(res.data.data || []))
        .catch(() => {});
    }
  };

  // Materials only have their Material ID units once a specific inventory
  // batch (and therefore a site) is chosen — mirrors handleMatInvSelect's
  // requiresMaterialId fetch for the main Issue form.
  const handleIssueRequestInventoryChange = (inventoryId) => {
    setIssueRequestInventoryId(inventoryId);
    setIssueRequestMaterialUnitId('');
    setIssueRequestMaterialUnits([]);
    const inv = inventory.find((r) => String(r.id) === String(inventoryId));
    if (inv?.kit?.requiresMaterialId) {
      materialUnitApi.list({ kitId: inv.kitId, siteId: inv.siteId, status: 'IN_STOCK' })
        .then((res) => setIssueRequestMaterialUnits(res.data.data || []))
        .catch(() => {});
    }
  };

  const handleIssueRequestSubmit = async (e) => {
    e.preventDefault();
    setIssueRequestSaving(true);
    setIssueRequestError('');
    try {
      const requiresMaterialId = issueRequestTarget.itemKind === 'MATERIAL'
        ? issueRequestTarget.kit?.requiresMaterialId
        : issueRequestTarget.itemKind === 'STATIONERY'
        ? issueRequestTarget.stationeryItem?.requiresMaterialId
        : issueRequestTarget.itItem?.requiresMaterialId;
      const body = issueRequestTarget.itemKind === 'MATERIAL'
        ? { inventoryId: parseInt(issueRequestInventoryId) }
        : { siteId: selectedSiteId };
      if (requiresMaterialId) body.materialUnitId = parseInt(issueRequestMaterialUnitId);
      if (issueRequestValidityWarn) body.confirmDeduction = issueRequestConfirmDeduction;
      if (issueRequestMaxQtyWarn) body.confirmMaxQtyDeduction = issueRequestConfirmMaxQtyDeduction;
      await workerRequestApi.issue(issueRequestTarget.id, body);
      setIssueRequestTarget(null);
      setRequestsLoaded(false); // refetch the Requests list
      await refresh();
    } catch (err) {
      const d = err.response?.data;
      if (d?.warn) { setIssueRequestValidityWarn(d); setIssueRequestError(''); }
      else if (d?.maxQtyWarn) { setIssueRequestMaxQtyWarn(d); setIssueRequestError(''); }
      else setIssueRequestError(d?.message || 'Error issuing item');
    } finally {
      setIssueRequestSaving(false);
    }
  };

  // ── Load pending damage reports when the Damage Approval tab is first opened ─
  const loadDamageReports = () => {
    setDamageReportsLoading(true);
    damageReportApi.list({ status: 'PENDING' })
      .then((res) => { setDamageReports(res.data.data || []); setDamageReportsLoaded(true); })
      .catch(() => {})
      .finally(() => setDamageReportsLoading(false));
  };
  useEffect(() => {
    if (activeTab !== 'damage-approval' || damageReportsLoaded) return;
    loadDamageReports();
  }, [activeTab, damageReportsLoaded]); // eslint-disable-line

  // Own-pending-flags visibility — available to any damage_flagging holder
  // (the backend scopes GET /api/damage-reports to flaggedById for non
  // damage_approval callers, so this is always just "my own flags").
  const loadPendingDamageFlags = () => {
    damageReportApi.list({ status: 'PENDING' })
      .then((res) => {
        const rows = res.data.data || [];
        setPendingDamageWorkerKitIds(new Set(rows.filter((r) => r.workerKitId).map((r) => r.workerKitId)));
        setPendingDamageStationeryIssuanceIds(new Set(rows.filter((r) => r.stationeryIssuanceId).map((r) => r.stationeryIssuanceId)));
        setPendingDamageLoaded(true);
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (activeTab !== 'workers' || pendingDamageLoaded) return;
    loadPendingDamageFlags();
  }, [activeTab, pendingDamageLoaded]); // eslint-disable-line

  // ── Flag an issued item, or un-issued stock, as damaged (opens the
  // photo-upload modal) ───────────────────────────────────────────────────────
  const openFlagDamaged = async ({ kind, target }) => {
    setDamageFlagTarget({ kind, target });
    setDamageFlagRemarks('');
    setDamageFlagFiles([]);
    setDamageFlagError('');
    setDamageFlagUnitIds([]);
    setDamageFlagAvailableUnits([]);
    if (kind === 'INVENTORY') {
      // `quantity` already excludes anything currently pending (deducted at
      // flag time on the backend), so no separate pending subtraction here.
      const available = Math.max(0, target.quantity - (target.damagedQuantity || 0));
      setDamageFlagQuantity(Math.min(1, available) || 0);
      if (target.kit?.requiresMaterialId) {
        try {
          const unitsRes = await materialUnitApi.list({ kitId: target.kitId, siteId: target.siteId, status: 'IN_STOCK' });
          setDamageFlagAvailableUnits(unitsRes.data.data || []);
        } catch { /* best-effort */ }
      }
    }
  };

  const handleFlagDamagedSubmit = async (e) => {
    e.preventDefault();
    // Photos are optional at flag time — they can be attached later, while
    // the report is still Pending, via handleAddDamagePhotos.
    if (damageFlagTarget.kind === 'INVENTORY' && damageFlagTarget.target.kit?.requiresMaterialId
      && damageFlagUnitIds.length !== damageFlagQuantity) {
      setDamageFlagError(`Select exactly ${damageFlagQuantity} Material ID(s)`);
      return;
    }
    setDamageFlagSaving(true);
    setDamageFlagError('');
    try {
      const formData = new FormData();
      if (damageFlagTarget.kind === 'MATERIAL') {
        formData.append('workerKitId', damageFlagTarget.target.id);
      } else {
        formData.append('inventoryId', damageFlagTarget.target.id);
        formData.append('quantity', damageFlagQuantity);
        damageFlagUnitIds.forEach((id) => formData.append('materialUnitIds', id));
      }
      if (damageFlagRemarks) formData.append('remarks', damageFlagRemarks);
      damageFlagFiles.forEach((f) => formData.append('photos', f));
      await damageReportApi.create(formData);
      setDamageFlagTarget(null);
      setDamageReportsLoaded(false); // refetch the Damage Approval queue for anyone who can see it
      setPendingDamageLoaded(false); // refetch so the flagger immediately sees "Pending Damage Approval" on this item
      // The Materials/Inventory tab's own "Pending" column reads pendingDamageQuantity
      // off the `inventory` state set by loadSiteData — that's a separate fetch from
      // the two above, so without this the flagged item's Pending count stays stale
      // until the next unrelated refresh (e.g. switching sites).
      await refresh();
    } catch (err) {
      setDamageFlagError(err.response?.data?.message || 'Error submitting damage report');
    } finally {
      setDamageFlagSaving(false);
    }
  };

  // A 403's raw body ("Permission required: damage_approval") is an internal
  // permission-key name, not something to show a user — translate it into a
  // plain, actionable message. Other statuses (400/404/etc) carry a message
  // that's actually meant to be read (e.g. "This report is no longer
  // pending"), so those pass through as-is.
  const friendlyErrorMessage = (err, fallback) => {
    if (err.response?.status === 403) return 'You do not have permission to perform this action.';
    return err.response?.data?.message || fallback;
  };

  const handleApproveDamage = async (id) => {
    setError('');
    try {
      await damageReportApi.approve(id);
      setDamageReports((prev) => prev.filter((r) => r.id !== id));
      setPendingDamageLoaded(false); // the flagger's "Pending Damage Approval" badge should clear now
      await refresh();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Error approving damage report'));
    }
  };

  // Pulls a still-Pending report back before an approver has acted — for the
  // store side (damage_flagging), not just the original flaggedById holder,
  // same authorization as attaching photos below.
  const [withdrawDamageTarget, setWithdrawDamageTarget] = useState(null); // { id, itemName }
  const handleWithdrawDamage = (id, itemName) => setWithdrawDamageTarget({ id, itemName });

  const submitWithdrawDamage = async () => {
    const { id } = withdrawDamageTarget;
    setWithdrawDamageTarget(null);
    setError('');
    try {
      await damageReportApi.withdraw(id);
      setDamageReports((prev) => prev.filter((r) => r.id !== id));
      setPendingDamageLoaded(false);
      await refresh();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Error withdrawing damage report'));
    }
  };

  // Attach photo(s) to a still-Pending damage report — covers the case where
  // none were uploaded at flag time (now optional, see handleFlagDamagedSubmit).
  const handleAddDamagePhotos = async (id, files) => {
    if (!files.length) return;
    setError('');
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('photos', f));
      const res = await damageReportApi.addPhotos(id, formData);
      setDamageReports((prev) => prev.map((r) => (r.id === id ? res.data.data : r)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Error attaching photo'));
    }
  };

  const handleRejectDamageSubmit = async (e) => {
    e.preventDefault();
    setRejectError('');
    try {
      await damageReportApi.reject(rejectTarget.id, { reviewRemarks: rejectRemarks });
      setDamageReports((prev) => prev.filter((r) => r.id !== rejectTarget.id));
      setPendingDamageLoaded(false); // ditto — the item becomes re-flaggable again
      setRejectTarget(null);
    } catch (err) {
      setRejectError(friendlyErrorMessage(err, 'Error rejecting damage report'));
    }
  };

  // ── Smart search — score workers by name tokens and employeeId ───────────────
  const smartSearchWorkers = (workers, query) => {
    const q = query.toLowerCase().trim();
    if (!q) return null;
    const queryTokens = q.split(/\s+/);

    const scoreWorker = (w) => {
      const name  = (w.name  || '').toLowerCase();
      const empId = (w.employeeId || '').toLowerCase();
      const nameParts = name.split(/\s+/);
      let s = 0;

      // Employee ID
      if (empId === q)               s += 100;
      else if (empId.startsWith(q))  s += 85;
      else if (empId.includes(q))    s += 55;

      // Full name
      if (name === q)                s += 100;
      else if (name.startsWith(q))   s += 90;
      else if (name.includes(q))     s += 65;

      // Per query-token vs name-part matching (handles partial first/last name)
      for (const tok of queryTokens) {
        for (const part of nameParts) {
          if (part === tok)           s += 45;
          else if (part.startsWith(tok)) s += 28;
          else if (part.includes(tok))   s += 12;
        }
      }

      return s;
    };

    return workers
      .map((w) => ({ w, score: scoreWorker(w) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ w }) => w);
  };

  // ── Assign worker to a site ──────────────────────────────────────────────────
  const handleAssignWorker = async (e) => {
    e.preventDefault();
    setAssignError('');
    try {
      await workerSiteAssignmentApi.create({
        workerId: assignTarget.id,
        siteId:   parseInt(assignSiteId),
      });
      setAssignTarget(null);
      setAssignSiteId('');
      if (parseInt(assignSiteId) === selectedSiteId) await refresh();
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Error assigning worker');
    }
  };

  // ── Generate Slip (A4 PDF, downloaded via the same blob pattern already
  // used for xlsx template downloads elsewhere in this codebase) ─────────────
  const [slipMenuTarget, setSlipMenuTarget] = useState(null);

  const handleGenerateSlip = async (worker, includePaid) => {
    setError('');
    setSlipMenuTarget(null);
    try {
      // siteId is only actually applied server-side for subcontractor-company
      // workers (who can be assigned to several sites at once) — everyone
      // else's slip is unaffected by it, so it's always safe to pass.
      const res = await workerApi.downloadSlip(worker.id, { includePaid, siteId: selectedSiteId });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `material_slip_${worker.name.replace(/[^a-z0-9]+/gi, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Error generating slip'); }
  };

  // ── Add Guest ────────────────────────────────────────────────────────────────
  // Live suggestions as the Employee ID field is typed — debounced, and only
  // fires while that field still has focus (closing over addGuestShowSuggestions
  // via the effect dependency avoids a stray fetch repopulating the list after
  // a suggestion was already picked or the field blurred).
  useEffect(() => {
    if (!showAddGuest || !addGuestShowSuggestions) return;
    const q = addGuestForm.employeeId.trim();
    if (q.length < 2) { setAddGuestSuggestions([]); return; }
    const timer = setTimeout(() => {
      workerApi.search(q)
        .then((res) => setAddGuestSuggestions(res.data.data || []))
        .catch(() => setAddGuestSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [showAddGuest, addGuestShowSuggestions, addGuestForm.employeeId]);

  const handlePickAddGuestSuggestion = async (s) => {
    setAddGuestForm({ ...addGuestForm, employeeId: s.employeeId, name: s.name });
    setAddGuestShowSuggestions(false);
    setAddGuestSuggestions([]);
    setAddGuestChecking(true);
    try {
      const res = await workerApi.lookup(s.employeeId);
      setAddGuestFoundWorker(res.data.data);
    } catch {
      setAddGuestFoundWorker(null);
    } finally {
      setAddGuestChecking(false);
    }
  };

  // Employee ID lookup — if it already belongs to someone, we'll reuse that
  // Worker (new site assignment tagged Guest) instead of creating a
  // duplicate record, so their material history/audit trail stays on the
  // one existing row and their home-site assignment is never touched.
  const handleAddGuestEmployeeIdBlur = async () => {
    const employeeId = addGuestForm.employeeId.trim();
    if (!employeeId) { setAddGuestFoundWorker(null); return; }
    setAddGuestChecking(true);
    try {
      const res = await workerApi.lookup(employeeId);
      setAddGuestFoundWorker(res.data.data);
    } catch {
      setAddGuestFoundWorker(null); // 404 — no existing employee, proceeds as a brand-new Guest
    } finally {
      setAddGuestChecking(false);
    }
  };

  const handleAddGuestSubmit = async (e) => {
    e.preventDefault();
    setAddGuestError('');
    setAddGuestSaving(true);
    try {
      let workerId;
      if (addGuestFoundWorker) {
        workerId = addGuestFoundWorker.id;
      } else {
        const created = await workerApi.create({
          name: addGuestForm.name,
          employeeId: addGuestForm.employeeId || undefined,
          role: addGuestForm.role || undefined,
          workerCategory: 'GUEST',
        });
        workerId = created.data.data.id;
      }
      // Either path ends with a Guest-tagged assignment at the currently
      // selected site — a brand-new Guest previously never got a site
      // assignment at all (a pre-existing gap), and an existing employee's
      // home assignment is left completely untouched (a separate row).
      await workerSiteAssignmentApi.create({ workerId, siteId: selectedSiteId, assignmentType: 'GUEST' });
      setShowAddGuest(false);
      setAddGuestForm({ name: '', employeeId: '', role: '' });
      setAddGuestFoundWorker(null);
      setAllWorkersLoaded(false); // force the Workers tab's worker list to refetch
      await refresh();
    } catch (err) {
      setAddGuestError(err.response?.data?.message || 'Error creating guest');
    } finally {
      setAddGuestSaving(false);
    }
  };

  // Badge shown next to a worker's name in the Workers tab. `assignmentType`
  // (when present) is the CURRENTLY SELECTED SITE's assignment type — an
  // existing Standard employee guesting at a second site shows "Guest" here
  // while still showing their normal type at their home site. Falls back to
  // the worker's own workerCategory (a true Guest/Subcontractor record) and
  // the derived employeeType for Contract Workers.
  const workerBadge = (w) => {
    if (w.assignmentType === 'GUEST') return { label: 'Guest', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
    if (w.workerCategory === 'GUEST') return { label: 'Guest', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
    if (w.workerCategory === 'SUBCONTRACTOR') return { label: 'Subcontractor', cls: 'bg-purple-100 text-purple-700 border-purple-200' };
    if (w.employeeType === 'CONTRACT') return { label: 'Contract', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
    return null;
  };

  // ── Issue modal ─────────────────────────────────────────────────────────────
  const openIssue = (worker) => {
    setIssueWorker(worker);
    setIssueType('material');
    setIssueMatType('ALL');
    setSelectedInv(null); setMatQty(1); setMatRemarks('');
    setValidityWarn(null); setConfirmDeduction(false);
    setMaxQtyWarn(null); setConfirmMaxQtyDeduction(false);
    setAvailableMaterialUnits([]); setSelectedMaterialUnitId('');
    setStatCategory('CONSUMABLE');
    setStatIssueItemId(''); setStatIssueQty(1); setStatIssueRemarks('');
    setStatIssueWarn(null); setStatConfirmDed(false);
    setStatMaxQtyWarn(null); setStatConfirmMaxQtyDed(false);
    setAvailableStatMaterialUnits([]); setSelectedStatMaterialUnitId('');
    setItIssueItemId(''); setItIssueQty(1); setItIssueRemarks('');
    setItIssueWarn(null); setItConfirmDed(false);
    setItMaxQtyWarn(null); setItConfirmMaxQtyDed(false);
    setAvailableItMaterialUnits([]); setSelectedItMaterialUnitId('');
    setError('');
  };

  // ── Validity check when material selected ───────────────────────────────────
  const handleInvSelect = async (invId) => {
    const inv = availableMatInv.find((b) => b.id === parseInt(invId));
    setSelectedInv(inv || null);
    setMatQty(1);
    setValidityWarn(null);
    setConfirmDeduction(false);
    setMaxQtyWarn(null);
    setConfirmMaxQtyDeduction(false);
    setAvailableMaterialUnits([]);
    setSelectedMaterialUnitId('');
    if (!inv || !issueWorker) return;
    try {
      const res = await workerKitApi.checkValidity({ workerId: issueWorker.id, kitId: inv.kitId });
      if (res.data?.warn) setValidityWarn(res.data);
    } catch { /* best-effort */ }
    if (inv.kit?.requiresMaterialId) {
      try {
        const unitsRes = await materialUnitApi.list({ kitId: inv.kitId, siteId: inv.siteId, status: 'IN_STOCK' });
        setAvailableMaterialUnits(unitsRes.data.data || []);
      } catch { /* best-effort */ }
    }
  };

  const handleStatItemSelect = async (itemId, itemsList) => {
    setStatIssueItemId(itemId);
    setStatIssueWarn(null); setStatConfirmDed(false);
    setStatMaxQtyWarn(null); setStatConfirmMaxQtyDed(false);
    setAvailableStatMaterialUnits([]); setSelectedStatMaterialUnitId('');
    const sinv = itemsList.find((i) => String(i.itemId) === String(itemId));
    if (sinv?.item?.requiresMaterialId && selectedSiteId) {
      try {
        const unitsRes = await materialUnitApi.list({ stationeryItemId: itemId, siteId: selectedSiteId, status: 'IN_STOCK' });
        setAvailableStatMaterialUnits(unitsRes.data.data || []);
      } catch { /* best-effort */ }
    }
  };

  const handleItItemSelect = async (itemId, itemsList) => {
    setItIssueItemId(itemId);
    setItIssueWarn(null); setItConfirmDed(false);
    setItMaxQtyWarn(null); setItConfirmMaxQtyDed(false);
    setAvailableItMaterialUnits([]); setSelectedItMaterialUnitId('');
    const iinv = itemsList.find((i) => String(i.itemId) === String(itemId));
    if (iinv?.item?.requiresMaterialId && selectedSiteId) {
      try {
        const unitsRes = await materialUnitApi.list({ itItemId: itemId, siteId: selectedSiteId, status: 'IN_STOCK' });
        setAvailableItMaterialUnits(unitsRes.data.data || []);
      } catch { /* best-effort */ }
    }
  };

  // ── Issue submissions ───────────────────────────────────────────────────────
  const handleMatIssueSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await workerKitApi.create({
        workerId:         issueWorker.id,
        inventoryId:      selectedInv.id,
        quantity:         parseInt(matQty),
        remarks:          matRemarks || null,
        confirmDeduction,
        confirmMaxQtyDeduction,
        materialUnitId:   selectedInv.kit?.requiresMaterialId ? parseInt(selectedMaterialUnitId) : undefined,
      });
      const wid = issueWorker.id;
      setIssueWorker(null);
      await afterAction(wid);
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.warn) setValidityWarn(d);
      else if (err.response?.status === 409 && d?.maxQtyWarn) setMaxQtyWarn(d);
      else setError(d?.message || 'Error issuing material');
    }
  };

  const handleStatIssueSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await stationeryApi.createIssuance({
        workerId:         issueWorker.id,
        itemId:           parseInt(statIssueItemId),
        siteId:           selectedSiteId,
        quantity:         parseInt(statIssueQty),
        remarks:          statIssueRemarks || null,
        confirmDeduction: statConfirmDed,
        confirmMaxQtyDeduction: statConfirmMaxQtyDed,
        materialUnitId: selectedStatItem?.requiresMaterialId ? parseInt(selectedStatMaterialUnitId) : undefined,
      });
      const wid = issueWorker.id;
      setIssueWorker(null);
      await afterAction(wid);
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.warn) setStatIssueWarn(d);
      else if (err.response?.status === 409 && d?.maxQtyWarn) setStatMaxQtyWarn(d);
      else setError(d?.message || 'Error issuing stationery item');
    }
  };

  const handleItIssueSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await itApi.createIssuance({
        workerId:         issueWorker.id,
        itemId:           parseInt(itIssueItemId),
        siteId:           selectedSiteId,
        quantity:         parseInt(itIssueQty),
        remarks:          itIssueRemarks || null,
        confirmDeduction: itConfirmDed,
        confirmMaxQtyDeduction: itConfirmMaxQtyDed,
        materialUnitId: selectedItItem?.requiresMaterialId ? parseInt(selectedItMaterialUnitId) : undefined,
      });
      const wid = issueWorker.id;
      setIssueWorker(null);
      await afterAction(wid);
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.warn) setItIssueWarn(d);
      else if (err.response?.status === 409 && d?.maxQtyWarn) setItMaxQtyWarn(d);
      else setError(d?.message || 'Error issuing IT item');
    }
  };

  // ── Return submissions ──────────────────────────────────────────────────────
  const handleMatReturn = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const needsBackfill = !matReturnTarget.materialUnit && matReturnTarget.kit?.requiresMaterialId;
      await workerKitApi.returnKit(matReturnTarget.id, {
        returnedQuantity: parseInt(matReturnForm.returnedQuantity),
        returnCondition:  'NORMAL',
        materialIdCodes: needsBackfill
          ? matReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      });
      setMatReturnTarget(null);
      setMatReturnMaterialIds('');
      await afterAction(expandedWorkerId);
    } catch (err) { setError(err.response?.data?.message || 'Error recording return'); }
  };

  const handleStatReturn = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const needsBackfill = !statReturnTarget.materialUnit && statReturnTarget.item?.requiresMaterialId;
      await stationeryApi.returnIssuance(statReturnTarget.id, {
        returnedQty: statReturnForm.returnedQty,
        returnCondition: 'NORMAL',
        materialIdCodes: needsBackfill
          ? statReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      });
      setStatReturnTarget(null);
      setStatReturnMaterialIds('');
      await afterAction(expandedWorkerId);
    } catch (err) { setError(err.response?.data?.message || 'Error recording return'); }
  };

  const handleItReturn = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const needsBackfill = !itReturnTarget.materialUnit && itReturnTarget.item?.requiresMaterialId;
      await itApi.returnIssuance(itReturnTarget.id, {
        returnedQty: itReturnForm.returnedQty,
        returnCondition: 'NORMAL',
        materialIdCodes: needsBackfill
          ? itReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      });
      setItReturnTarget(null);
      setItReturnMaterialIds('');
      await afterAction(expandedWorkerId);
    } catch (err) { setError(err.response?.data?.message || 'Error recording return'); }
  };

  // ── Add / Edit stock (stationery) ────────────────────────────────────────────
  const [editStatInvId, setEditStatInvId] = useState(null);
  const [showAddStatStockConfirm, setShowAddStatStockConfirm] = useState(false);
  const [addStatStockMaterialIds, setAddStatStockMaterialIds] = useState('');
  const addStatItem = allStatItems.find((it) => it.id === parseInt(addStatStockForm.itemId));

  const submitAddStatStock = async () => {
    setAddStatStockError('');
    try {
      if (editStatInvId) {
        await stationeryApi.updateInventory(editStatInvId, {
          quantity: parseInt(addStatStockForm.quantity),
        });
      } else {
        const materialIdCodes = addStatItem?.requiresMaterialId
          ? addStatStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined;
        await stationeryApi.adjustInventory({
          siteId:   selectedSiteId,
          itemId:   parseInt(addStatStockForm.itemId),
          quantity: parseInt(addStatStockForm.quantity),
          materialIdCodes,
        });
      }
      setShowAddStatStockConfirm(false);
      setShowAddStatStock(false);
      setEditStatInvId(null);
      setAddStatStockForm({ itemId: '', quantity: '' });
      setAddStatStockMaterialIds('');
      await refresh();
    } catch (err) {
      setAddStatStockError(err.response?.data?.message || 'Error saving stock');
    }
  };

  // ── Add / Edit stock (IT) ────────────────────────────────────────────────────
  const [editItInvId, setEditItInvId] = useState(null);
  const [showAddItStockConfirm, setShowAddItStockConfirm] = useState(false);
  const [addItStockMaterialIds, setAddItStockMaterialIds] = useState('');
  const addItItem = allItItems.find((it) => it.id === parseInt(addItStockForm.itemId));

  const submitAddItStock = async () => {
    setAddItStockError('');
    try {
      if (editItInvId) {
        await itApi.updateInventory(editItInvId, {
          quantity: parseInt(addItStockForm.quantity),
        });
      } else {
        const materialIdCodes = addItItem?.requiresMaterialId
          ? addItStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined;
        await itApi.adjustInventory({
          siteId:   selectedSiteId,
          itemId:   parseInt(addItStockForm.itemId),
          quantity: parseInt(addItStockForm.quantity),
          materialIdCodes,
        });
      }
      setShowAddItStockConfirm(false);
      setShowAddItStock(false);
      setEditItInvId(null);
      setAddItStockForm({ itemId: '', quantity: '' });
      setAddItStockMaterialIds('');
      await refresh();
    } catch (err) {
      setAddItStockError(err.response?.data?.message || 'Error saving stock');
    }
  };

  // ── Manage Material IDs — assign IDs to already-existing stock that
  // predates requiresMaterialId being turned on, or rename an existing code.
  // Shared by the Materials, Stationery, and IT tabs (kitId xor
  // stationeryItemId xor itItemId). See materialUnitApi.create/rename.
  const [manageIdsTarget, setManageIdsTarget] = useState(null); // { kitId?, stationeryItemId?, itItemId?, siteId, name, aggQty }
  const [manageIdsUnits, setManageIdsUnits] = useState(null); // null = loading
  const [manageIdsNewCodes, setManageIdsNewCodes] = useState('');
  const [manageIdsError, setManageIdsError] = useState('');
  const [manageIdsSaving, setManageIdsSaving] = useState(false);

  // Fetches every unit regardless of status (issued/damaged/in-repair units
  // are still meaningful to browse), but shortfall math below stays scoped
  // to IN_STOCK only — that's what the backend's own shortfall check compares
  // against (aggQty is an in-stock count), so mixing in other statuses here
  // would silently under/over-count how many new codes can be assigned.
  const loadManageIdsUnits = async (target) => {
    setManageIdsUnits(null);
    try {
      const res = await materialUnitApi.list({
        kitId: target.kitId, stationeryItemId: target.stationeryItemId, itItemId: target.itItemId, siteId: target.siteId, status: 'ALL',
      });
      setManageIdsUnits(res.data.data);
    } catch {
      setManageIdsUnits([]);
    }
  };

  const openManageIds = (target) => {
    setManageIdsTarget(target);
    setManageIdsNewCodes('');
    setManageIdsError('');
    loadManageIdsUnits(target);
  };

  const submitManageIdsAssign = async () => {
    setManageIdsSaving(true);
    setManageIdsError('');
    try {
      const codes = manageIdsNewCodes.split('\n').map((s) => s.trim()).filter(Boolean);
      await materialUnitApi.create({
        kitId: manageIdsTarget.kitId, stationeryItemId: manageIdsTarget.stationeryItemId, itItemId: manageIdsTarget.itItemId,
        siteId: manageIdsTarget.siteId, codes,
      });
      setManageIdsNewCodes('');
      await loadManageIdsUnits(manageIdsTarget);
      await refresh();
    } catch (err) {
      setManageIdsError(err.response?.data?.message || 'Failed to assign Material IDs');
    } finally {
      setManageIdsSaving(false);
    }
  };


  const handleAddStatStockSubmit = async (e) => {
    e.preventDefault();
    // Same rule as materials: only the add path (not editing an existing
    // entry) can trigger the extra confirmation.
    if (!editStatInvId && hasPermission('stationery_add') && !hasPermission('stationery_edit')) {
      setShowAddStatStockConfirm(true);
      return;
    }
    await submitAddStatStock();
  };

  const handleAddItStockSubmit = async (e) => {
    e.preventDefault();
    if (!editItInvId && hasPermission('it_add') && !hasPermission('it_edit')) {
      setShowAddItStockConfirm(true);
      return;
    }
    await submitAddItStock();
  };

  // ── Add / Edit stock (materials) ─────────────────────────────────────────────
  const addStockKit = allKits.find((k) => k.id === parseInt(addStockForm.kitId));
  const [showAddStockConfirm, setShowAddStockConfirm] = useState(false);

  const submitAddStock = async () => {
    setError('');
    try {
      if (editInvId) {
        await inventoryApi.update(editInvId, {
          // The form holds "In Stock" (see openEdit above) — add the
          // damaged quantity back so the raw StoreInventory.quantity saved
          // still nets out to the value the user actually typed.
          quantity:     parseInt(addStockForm.quantity) + editInvDamagedQty,
          receivedDate: addStockForm.receivedDate || null,
          remarks:      addStockForm.remarks || null,
        });
      } else {
        const materialIdCodes = addStockKit?.requiresMaterialId
          ? addStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined;
        await inventoryApi.create({
          siteId:       selectedSiteId,
          kitId:        parseInt(addStockForm.kitId),
          quantity:     parseInt(addStockForm.quantity),
          receivedDate: addStockForm.receivedDate || null,
          remarks:      addStockForm.remarks || null,
          materialIdCodes,
        });
      }
      setShowAddStockConfirm(false);
      setShowAddStock(false);
      setEditInvId(null);
      setAddStockMaterialIds('');
      await refresh();
    } catch (err) { setError(err.response?.data?.message || 'Error saving stock'); }
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    // Only the add path (not editing an existing entry) can trigger the
    // extra confirmation — someone who can edit doesn't need the warning
    // since they can still correct the entry afterwards.
    if (!editInvId && hasPermission('inventory_add') && !hasPermission('inventory_edit')) {
      setShowAddStockConfirm(true);
      return;
    }
    await submitAddStock();
  };


  // ── Repair & Maintenance workflow (Stationery Durables, and — via
  // itemType — IT Durables) ────────────────────────────────────────────────
  // Step 1 — "Mark as Needs Repair": flag a Durable (issued to a worker, or
  // un-issued stock) as needing repair. Opens the picker modal for the
  // un-issued-stock + Material-ID case; issued items need no extra input.
  const [repairFlagTarget, setRepairFlagTarget] = useState(null); // { kind: 'ISSUANCE'|'INVENTORY', target, itemType: 'STATIONERY'|'IT' }
  const [repairFlagQuantity, setRepairFlagQuantity] = useState(1);
  const [repairFlagAvailableUnits, setRepairFlagAvailableUnits] = useState([]);
  const [repairFlagUnitIds, setRepairFlagUnitIds] = useState([]);
  const [repairFlagError, setRepairFlagError] = useState('');
  const [repairFlagSaving, setRepairFlagSaving] = useState(false);

  // itemType defaults to 'STATIONERY' so every pre-existing call site (which
  // never passes it) behaves exactly as before.
  const openRepairFlag = async ({ kind, target, itemType = 'STATIONERY' }) => {
    setRepairFlagTarget({ kind, target, itemType });
    setRepairFlagError('');
    setRepairFlagUnitIds([]);
    setRepairFlagAvailableUnits([]);
    if (kind === 'INVENTORY') {
      setRepairFlagQuantity(Math.min(1, target.quantity) || 0);
      if (target.item?.requiresMaterialId) {
        try {
          const unitsRes = await materialUnitApi.list(
            itemType === 'IT'
              ? { itItemId: target.itemId, siteId: target.siteId, status: 'IN_STOCK' }
              : { stationeryItemId: target.itemId, siteId: target.siteId, status: 'IN_STOCK' }
          );
          setRepairFlagAvailableUnits(unitsRes.data.data || []);
        } catch { /* best-effort */ }
      }
    }
  };

  const handleRepairFlagSubmit = async (e) => {
    e.preventDefault();
    if (repairFlagTarget.kind === 'INVENTORY' && repairFlagTarget.target.item?.requiresMaterialId
      && repairFlagUnitIds.length !== repairFlagQuantity) {
      setRepairFlagError(`Select exactly ${repairFlagQuantity} Material ID(s)`);
      return;
    }
    setRepairFlagSaving(true);
    setRepairFlagError('');
    try {
      const isIT = repairFlagTarget.itemType === 'IT';
      const data = repairFlagTarget.kind === 'ISSUANCE'
        ? (isIT ? { itIssuanceId: repairFlagTarget.target.id } : { stationeryIssuanceId: repairFlagTarget.target.id })
        : (isIT
            ? { itInventoryId: repairFlagTarget.target.id, quantity: repairFlagQuantity, materialUnitIds: repairFlagUnitIds }
            : { stationeryInventoryId: repairFlagTarget.target.id, quantity: repairFlagQuantity, materialUnitIds: repairFlagUnitIds });
      await repairTicketApi.create(data);
      setRepairFlagTarget(null);
      await refresh();
    } catch (err) {
      setRepairFlagError(err.response?.data?.message || 'Error marking as needs repair');
    } finally {
      setRepairFlagSaving(false);
    }
  };

  // Step 2 — "Sent for Maintenance"
  const handleSendForMaintenance = async (id) => {
    setError('');
    try { await repairTicketApi.sendForMaintenance(id); await refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Error sending for maintenance'); }
  };

  // Step 3 — "Returned from Maintenance" (restocks as Repaired)
  const handleReturnFromMaintenance = async (id) => {
    setError('');
    try { await repairTicketApi.returnFromMaintenance(id); await refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Error returning from maintenance'); }
  };

  // "Beyond Recovery" — permanent, no restock
  const [scrapTicketTarget, setScrapTicketTarget] = useState(null); // { id, itemName }
  const handleScrapTicket = (id, itemName) => setScrapTicketTarget({ id, itemName });

  const submitScrapTicket = async () => {
    const { id } = scrapTicketTarget;
    setScrapTicketTarget(null);
    setError('');
    try { await repairTicketApi.scrap(id); await refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Error marking scrapped'); }
  };

  // ── Status detail modal — "Needs Repair" / "Scrapped" click-through ────────
  // Resolves Material ID codes for any serialized tickets in the list, since
  // RepairTicket only stores materialUnitIds (ints), not the codes.
  const [statusDetailModal, setStatusDetailModal] = useState(null); // { title, tickets }
  const [statusDetailCodes, setStatusDetailCodes] = useState({}); // materialUnitId -> code

  const openStatusDetail = async (title, tickets) => {
    setStatusDetailModal({ title, tickets });
    const allUnitIds = [...new Set(tickets.flatMap((t) => t.materialUnitIds || []))];
    if (allUnitIds.length === 0) { setStatusDetailCodes({}); return; }
    try {
      const res = await materialUnitApi.byIds(allUnitIds);
      const map = {};
      (res.data.data || []).forEach((u) => { map[u.id] = u; });
      setStatusDetailCodes(map);
    } catch { setStatusDetailCodes({}); }
  };

  // ── Material ID lifecycle history modal ─────────────────────────────────────
  const [historyModal, setHistoryModal] = useState(null); // { unit, events } | 'loading'
  const [historyExpanded, setHistoryExpanded] = useState(false); // history is collapsed by default; details (values) show first
  // Renaming a Material ID code is only reachable from here — after opening
  // a specific unit's details — not from the unit list itself.
  const [detailRenaming, setDetailRenaming] = useState(false);
  const [detailRenameValue, setDetailRenameValue] = useState('');
  const [detailRenameSaving, setDetailRenameSaving] = useState(false);
  const [detailRenameError, setDetailRenameError] = useState('');
  // Set only when Material Details was opened from the "Material ID Units"
  // list — lets that Back button return there instead of just closing, so
  // picking a different unit doesn't mean closing the tab and starting over.
  // Left null for every other entry point (issued-history, damage/repair
  // status detail, etc.), which have no such list to go back to.
  const [detailBackTarget, setDetailBackTarget] = useState(null);
  const openMaterialHistory = async (materialUnitId) => {
    setHistoryModal('loading');
    setHistoryExpanded(false);
    setDetailRenaming(false);
    setDetailRenameError('');
    setDetailBackTarget(null); // cleared here; the units-list entry point re-sets it right after calling this
    try {
      const res = await materialUnitApi.history(materialUnitId);
      setHistoryModal(res.data.data);
    } catch (err) {
      setHistoryModal(null);
      setError(err.response?.data?.message || 'Error loading material history');
    }
  };
  const submitDetailRename = async () => {
    setDetailRenameSaving(true);
    setDetailRenameError('');
    try {
      const newCode = detailRenameValue.trim();
      await materialUnitApi.rename(historyModal.unit.id, { materialIdCode: newCode });
      setHistoryModal((prev) => ({ ...prev, unit: { ...prev.unit, materialIdCode: newCode } }));
      setDetailRenaming(false);
    } catch (err) {
      setDetailRenameError(err.response?.data?.message || 'Failed to rename Material ID');
    } finally {
      setDetailRenameSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  // ── Derived state ───────────────────────────────────────────────────────────
  const selectedSite    = sites.find((s) => s.id === selectedSiteId);
  const workerMap       = Object.fromEntries(siteAssignments.map((a) => [a.workerId, a.worker]).filter(([, w]) => w));
  // The assignment's own nested `worker` is a bare {id, name, employeeId} —
  // it doesn't carry workerCategory/employeeType, so workerBadge() would never
  // see GUEST/SUBCONTRACTOR/CONTRACT here. allWorkers (loaded via
  // workerApi.list() above) has the full record; merge it in by id.
  const allWorkersById  = Object.fromEntries(allWorkers.map((w) => [w.id, w]));
  // assignmentType attached here reflects THIS site's assignment specifically
  // (see workerBadge) — a worker with two assignments (home + guest) shows
  // differently depending on which site's list they're being rendered in.
  const siteWorkerList  = [...new Map(siteAssignments.map((a) => [a.workerId, a.worker ? { ...allWorkersById[a.workerId], ...a.worker, assignmentType: a.assignmentType } : null])).values()].filter(Boolean);

  const issuedByKit = {};
  siteWorkerKits.forEach((wk) => {
    issuedByKit[wk.kitId] = (issuedByKit[wk.kitId] || 0) + (wk.quantity - (wk.returnedQuantity || 0));
  });

  // Open pipeline (shown in the "Repaired — In the Repair & Maintenance
  // Pipeline" table) — everything not yet resolved to REPAIRED/SCRAPPED.
  // repairTickets itself may hold BOTH Stationery-sourced (t.itemId set) and
  // IT-sourced (t.itItemId set) rows — see repairTickets.js list(), which
  // returns whichever side(s) the caller's repair_management/it_repair_management
  // holds — so every table below must filter to its own side explicitly.
  const openRepairTickets     = repairTickets.filter((t) => ['PENDING_REPAIR', 'UNDER_MAINTENANCE'].includes(t.status));
  const openStatRepairTickets = openRepairTickets.filter((t) => t.itemId != null);
  const openItRepairTickets   = openRepairTickets.filter((t) => t.itItemId != null);

  const needsRepairByItem = {};   // itemId -> qty currently NEEDS_REPAIR (PENDING_REPAIR only) — Stationery
  const underRepairByItem = {};   // itemId -> qty anywhere in the open pipeline (both stages) — Stationery, kept for reference
  const needsRepairByItItemId = {}; // same as above, for IT
  const underRepairByItItemId = {};
  // Scrapped items are no longer surfaced here — Asset History is now the
  // canonical place to see scrap history, so we don't duplicate it in this table.
  const repairStatusByStationeryIssuanceId = new Map(); // issuanceId -> 'PENDING_REPAIR'|'UNDER_MAINTENANCE'
  const repairStatusByItIssuanceId = new Map();
  repairTickets.forEach((t) => {
    const isIT = t.itItemId != null;
    const qty = t.quantity ?? t.stationeryIssuance?.quantity ?? t.itIssuance?.quantity ?? 1;
    if (isIT) {
      if (t.status === 'PENDING_REPAIR') needsRepairByItItemId[t.itItemId] = (needsRepairByItItemId[t.itItemId] || 0) + qty;
      if (['PENDING_REPAIR', 'UNDER_MAINTENANCE'].includes(t.status)) {
        underRepairByItItemId[t.itItemId] = (underRepairByItItemId[t.itItemId] || 0) + qty;
        if (t.itIssuanceId) repairStatusByItIssuanceId.set(t.itIssuanceId, t.status);
      }
    } else {
      if (t.status === 'PENDING_REPAIR') needsRepairByItem[t.itemId] = (needsRepairByItem[t.itemId] || 0) + qty;
      if (['PENDING_REPAIR', 'UNDER_MAINTENANCE'].includes(t.status)) {
        underRepairByItem[t.itemId] = (underRepairByItem[t.itemId] || 0) + qty;
        if (t.stationeryIssuanceId) repairStatusByStationeryIssuanceId.set(t.stationeryIssuanceId, t.status);
      }
    }
  });

  const statInvFiltered     = statInventory.filter((i) => i.item?.category === statCategoryFilter);
  const durableIssuances    = statIssuances.filter((iss) => iss.item?.category === 'DURABLE');
  const consumableIssuances = statIssuances.filter((iss) => iss.item?.category === 'CONSUMABLE');

  const itInvFiltered         = itInventory.filter((i) => i.item?.category === itCategoryFilter);
  const itDurableIssuances    = itIssuances.filter((iss) => iss.item?.category === 'DURABLE');
  const itConsumableIssuances = itIssuances.filter((iss) => iss.item?.category === 'CONSUMABLE');

  const matInvFiltered = matTypeFilter === 'ALL' ? inventory : inventory.filter((r) => r.kit?.type === matTypeFilter);

  // A storekeeper assigned to more than one site sees combined inventory
  // across all of them (single-site selector doesn't apply) — show which
  // site each row belongs to only when that's actually ambiguous.
  const matShowSiteCol  = new Set(inventory.map((r) => r.site?.id)).size > 1;
  const statShowSiteCol = new Set(statInventory.map((r) => r.site?.id)).size > 1;
  const itShowSiteCol   = new Set(itInventory.map((r) => r.site?.id)).size > 1;

  // Issue modal derived values
  const availableMatInv = inventory.filter((r) => (r.quantity - (r.damagedQuantity || 0)) > 0);
  const availableMatInvForIssue = issueMatType === 'ALL'
    ? availableMatInv
    : availableMatInv.filter((r) => r.kit?.type === issueMatType);
  const availableForIssue = selectedInv
    ? Math.max(0, (selectedInv.quantity || 0) - (selectedInv.damagedQuantity || 0))
    : 0;

  const statItemsForIssue = allStatItems
    .filter((item) => item.category === statCategory)
    .map((item) => {
      const inv = statInventory.find((sinv) => sinv.itemId === item.id);
      return { itemId: item.id, item, quantity: inv?.quantity || 0 };
    });
  const selectedStatInv  = statItemsForIssue.find((sinv) => sinv.itemId === parseInt(statIssueItemId));
  const statItemStock    = selectedStatInv?.quantity || 0;
  const selectedStatItem = selectedStatInv?.item;

  const itItemsForIssue = allItItems
    .filter((item) => item.category === itCategory)
    .map((item) => {
      const inv = itInventory.find((iinv) => iinv.itemId === item.id);
      return { itemId: item.id, item, quantity: inv?.quantity || 0 };
    });
  const selectedItInv  = itItemsForIssue.find((iinv) => iinv.itemId === parseInt(itIssueItemId));
  const itItemStock    = selectedItInv?.quantity || 0;
  const selectedItItem = selectedItInv?.item;

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle={selectedSite ? selectedSite.name : 'Select a site to view inventory'}
        action={
          activeTab === 'materials' && hasPermission('inventory_add') ? (
            <div className="flex items-center gap-2">
              {selectedSiteId && (
                <ExcelUploadMenu
                  loading={bulkStockLoading}
                  onDownloadTemplate={handleMaterialsBulkTemplate}
                  onUploadClick={() => materialsBulkFileRef.current?.click()}
                />
              )}
              <input
                ref={materialsBulkFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleMaterialsBulkUploadChange}
              />
              <button
                className="btn-primary"
                onClick={() => { setAddStockForm({ kitId: '', quantity: '', receivedDate: todayLocalDate(), remarks: '' }); setEditInvId(null); setError(''); setAddStockMaterialIds(''); setShowAddStock(true); }}
              >
                + Add Stock
              </button>
            </div>
          ) : activeTab === 'stationery' && hasPermission('stationery_add') ? (
            <div className="flex items-center gap-2">
              {selectedSiteId && (
                <ExcelUploadMenu
                  loading={bulkStockLoading}
                  onDownloadTemplate={handleStationeryBulkTemplate}
                  onUploadClick={() => stationeryBulkFileRef.current?.click()}
                />
              )}
              <input
                ref={stationeryBulkFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleStationeryBulkUploadChange}
              />
              <button
                className="btn-primary"
                onClick={() => { setAddStatStockForm({ itemId: '', quantity: '' }); setEditStatInvId(null); setAddStatStockError(''); setShowAddStatStock(true); }}
              >
                + Add Stock
              </button>
            </div>
          ) : activeTab === 'it' && canViewIT && hasPermission('it_add') ? (
            <button
              className="btn-primary"
              onClick={() => { setAddItStockForm({ itemId: '', quantity: '' }); setEditItInvId(null); setAddItStockError(''); setShowAddItStock(true); }}
            >
              + Add Stock
            </button>
          ) : activeTab === 'workers' && hasPermission('guest_management') ? (
            <button
              className="btn-primary"
              onClick={() => { setAddGuestForm({ name: '', employeeId: '', role: '' }); setAddGuestError(''); setAddGuestFoundWorker(null); setShowAddGuest(true); }}
            >
              + Add Guest
            </button>
          ) : null
        }
      />

      {!isStorekeeper && (
        <div className="mb-4">
          <div className="w-56">
            <SearchableSelect
              options={sites.map((s) => ({ id: s.id, label: s.name }))}
              value={selectedSiteId || ''}
              onChange={(id) => handleSiteChange(id)}
              placeholder="Select site…"
            />
          </div>
        </div>
      )}

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      {/* Tab bar */}
      <div className="flex gap-1 mb-6">
        {[
          // Each pillar is gated behind its own least-privilege view
          // permission alone — holding a different pillar's permission does
          // NOT surface this tab. When the relevant *_view permission is
          // absent the tab is not rendered at all (not just disabled),
          // matching every other permission-gated tab/nav item in this app.
          ...(canViewMaterials ? [['materials', 'Materials']] : []),
          ...(canViewStationery ? [['stationery', 'Stationery']] : []),
          // IT is deliberately isolated behind it_view alone — holding
          // inventory_view/stationery_manage/etc. does NOT surface this tab.
          // When it_view is absent the tab is not rendered at all (not just
          // disabled), matching every other permission-gated tab/nav item
          // in this app.
          ...(canViewIT ? [['it', 'IT']] : []),
          ['workers', 'Workers'],
          ...(hasPermission('request_management') ? [['requests', 'Requests']] : []),
          ...(hasPermission('damage_approval') || hasPermission('damage_flagging') ? [['damage-approval', 'Damage Approval']] : []),
        ].map(([t, l]) => (
          <button key={t} className={activeTab === t ? 'btn-primary' : 'btn-secondary'} onClick={() => setActiveTab(t)}>
            {l}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MATERIALS TAB
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'materials' && canViewMaterials && (
        <>
          <div className="flex gap-1 mb-5 flex-wrap">
            <button className={matTypeFilter === 'ALL' ? 'btn-primary' : 'btn-secondary'} onClick={() => setMatTypeFilter('ALL')}>All</button>
            {MATERIAL_TYPES.map((t) => (
              <button key={t.value} className={matTypeFilter === t.value ? 'btn-primary' : 'btn-secondary'} onClick={() => setMatTypeFilter(t.value)}>{t.label}</button>
            ))}
          </div>
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Material</th>
                {matShowSiteCol && <th>Site</th>}
                <th>Type</th>
                <th>In Stock</th>
                <th>Pending Damage Approval</th>
                <th>Issued</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matInvFiltered.map((r) => {
                const issued    = issuedByKit[r.kitId] || 0;
                // r.quantity is already net of issued stock — it's decremented
                // at issue time (see workerKits.js create()) and incremented
                // back on return. Subtracting `issued` again here double-counts
                // it; `issued` is purely informational for the separate Issued
                // column below.
                const available = Math.max(0, (r.quantity || 0) - (r.damagedQuantity || 0));
                return (
                  <tr key={r.id}>
                    <td>
                      {r.kit?.requiresMaterialId ? (
                        <button
                          className="font-medium text-blue-600 hover:underline text-left"
                          onClick={() => openManageIds({ kitId: r.kitId, siteId: r.siteId, name: r.kit.name, aggQty: available })}
                          title="View Material ID units"
                        >
                          {r.kit?.name}
                        </button>
                      ) : (
                        <p className="font-medium">{r.kit?.name}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        {r.kit?.unit}
                        {r.kit?.validityDays ? ` · ${r.kit.validityDays}d validity` : ''}
                        {r.kit?.price ? ` · ₹${parseFloat(r.kit.price).toFixed(0)}` : ''}
                      </p>
                    </td>
                    {matShowSiteCol && <td className="text-xs text-gray-500">{r.site?.name || '—'}</td>}
                    <td className="text-xs text-gray-500">{materialTypeLabel(r.kit?.type)}</td>
                    <td>
                      <button
                        className={`font-semibold text-lg hover:underline ${available <= 0 ? 'text-red-600' : 'text-green-600'}`}
                        onClick={() => openStockHistory('MATERIAL', r)}
                        title="View stock addition history"
                      >
                        {available}
                      </button>
                    </td>
                    <td>
                      {(r.pendingDamageQuantity || 0) > 0
                        ? <span className="text-amber-600 font-medium">{r.pendingDamageQuantity}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td>
                      {issued > 0 ? (
                        <button
                          className="text-indigo-600 font-semibold hover:underline"
                          onClick={() => openKitIssuedHistory(r)}
                        >
                          {issued}
                        </button>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="text-gray-400 text-xs">
                      {r.receivedDate ? new Date(r.receivedDate).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td>
                      <div className="flex gap-2 flex-wrap">
                        {hasPermission('damage_flagging') && (() => {
                          const pendingQty = r.pendingDamageQuantity || 0;
                          const dmgAvailable = (r.quantity || 0) - (r.damagedQuantity || 0);
                          return (
                            <button
                              className="text-orange-500 hover:underline text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                              disabled={dmgAvailable <= 0}
                              title={pendingQty > 0 ? `${pendingQty} unit(s) pending damage approval` : undefined}
                              onClick={() => openFlagDamaged({ kind: 'INVENTORY', target: r })}
                            >
                              Report Damaged
                            </button>
                          );
                        })()}
                        {hasPermission('inventory_edit') && (
                          <button
                            className="text-blue-600 hover:underline text-sm"
                            onClick={() => {
                              const inStock = Math.max(0, (r.quantity || 0) - (r.damagedQuantity || 0));
                              setAddStockForm({ kitId: r.kitId, quantity: inStock, receivedDate: r.receivedDate?.split('T')[0] || '', remarks: r.remarks || '' });
                              setEditInvId(r.id);
                              setEditInvDamagedQty(r.damagedQuantity || 0);
                              setError('');
                              setShowAddStock(true);
                            }}
                          >
                            Edit
                          </button>
                        )}
                        {r.kit?.requiresMaterialId && hasPermission('inventory_edit') && (
                          <button
                            className="text-purple-600 hover:underline text-sm"
                            onClick={() => openManageIds({ kitId: r.kitId, siteId: r.siteId, name: r.kit.name, aggQty: available })}
                          >
                            Manage IDs
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {matInvFiltered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">
                  {inventory.length === 0
                    ? 'No material inventory at this site. Use "+ Add Stock" to add materials.'
                    : 'No materials of this type at this site.'}
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          STATIONERY TAB
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'stationery' && canViewStationery && (
        <>
          <div className="flex gap-1 mb-5">
            {[['CONSUMABLE', 'Consumable'], ['DURABLE', 'Durable']].map(([v, l]) => (
              <button key={v} className={statCategoryFilter === v ? 'btn-primary' : 'btn-secondary'} onClick={() => setStatCategoryFilter(v)}>{l}</button>
            ))}
          </div>

          <SectionHeader title="Stock Levels" />
          <div className="table-container mb-6">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  {statShowSiteCol && <th>Site</th>}
                  <th>In Stock</th>
                  <th>Issued</th>
                  {statCategoryFilter === 'DURABLE' && <th>Needs Repair</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {statInvFiltered.map((sinv) => {
                  const issued     = (statCategoryFilter === 'DURABLE' ? durableIssuances : consumableIssuances)
                    .filter((i) => i.itemId === sinv.itemId).reduce((s, i) => s + i.quantity, 0);
                  const needsRepair = needsRepairByItem[sinv.itemId] || 0;
                  return (
                    <tr key={sinv.id}>
                      <td>
                        {sinv.item?.requiresMaterialId ? (
                          <button
                            className="font-medium text-blue-600 hover:underline text-left"
                            onClick={() => openManageIds({ stationeryItemId: sinv.itemId, siteId: sinv.siteId, name: sinv.item.name, aggQty: sinv.quantity })}
                            title="View Material ID units"
                          >
                            {sinv.item?.name}
                          </button>
                        ) : (
                          <p className="font-medium">{sinv.item?.name}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {sinv.item?.unit}
                          {sinv.item?.price ? ` · ₹${parseFloat(sinv.item.price).toFixed(0)}` : ''}
                        </p>
                      </td>
                      {statShowSiteCol && <td className="text-xs text-gray-500">{sinv.site?.name || '—'}</td>}
                      <td>
                        <button
                          className={`font-semibold text-lg hover:underline ${sinv.quantity <= 0 ? 'text-red-600' : 'text-gray-900'}`}
                          onClick={() => openStockHistory('STATIONERY', sinv)}
                          title="View stock addition history"
                        >
                          {sinv.quantity}
                        </button>
                      </td>
                      <td>
                        {issued > 0 ? (
                          <button
                            className="font-semibold text-indigo-600 hover:underline"
                            onClick={() => openStatIssuedHistory(sinv)}
                          >
                            {issued}
                          </button>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      {statCategoryFilter === 'DURABLE' && (
                        <td>
                          {needsRepair > 0 ? (
                            <button
                              className="font-medium text-orange-500 hover:underline"
                              onClick={() => openStatusDetail(
                                `Needs Repair — ${sinv.item?.name}`,
                                repairTickets.filter((t) => t.itemId === sinv.itemId && t.status === 'PENDING_REPAIR')
                              )}
                            >
                              {needsRepair}
                            </button>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      )}
                      <td>
                        <div className="flex gap-2 flex-wrap">
                          {statCategoryFilter === 'DURABLE' && hasPermission('repair_management') && (
                            <button
                              className="text-orange-500 hover:underline text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                              disabled={sinv.quantity <= 0}
                              onClick={() => openRepairFlag({ kind: 'INVENTORY', target: sinv })}
                            >
                              Mark as Needs Repair
                            </button>
                          )}
                          {hasPermission('stationery_edit') && (
                            <button
                              className="text-blue-600 hover:underline text-sm"
                              onClick={() => { setAddStatStockForm({ itemId: sinv.itemId, quantity: sinv.quantity }); setEditStatInvId(sinv.id); setAddStatStockError(''); setShowAddStatStock(true); }}
                            >
                              Edit
                            </button>
                          )}
                          {sinv.item?.requiresMaterialId && hasPermission('stationery_edit') && (
                            <button
                              className="text-purple-600 hover:underline text-sm"
                              onClick={() => openManageIds({ stationeryItemId: sinv.itemId, siteId: sinv.siteId, name: sinv.item.name, aggQty: sinv.quantity })}
                            >
                              Manage IDs
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {statInvFiltered.length === 0 && (
                  <tr><td colSpan={(statShowSiteCol ? 1 : 0) + (statCategoryFilter === 'DURABLE' ? 6 : 4)} className="text-center py-8 text-gray-400">
                    No {statCategoryFilter === 'CONSUMABLE' ? 'consumable' : 'durable'} inventory at this site
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {statCategoryFilter === 'DURABLE' && (
            <>
              <SectionHeader title="Repair & Maintenance Pipeline" />
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Marked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openStatRepairTickets.map((t) => (
                      <tr key={t.id}>
                        <td>
                          {t.stationeryIssuance
                            ? <>
                                <p className="font-medium text-sm">{t.stationeryIssuance.worker?.name}</p>
                                <p className="text-xs text-gray-400 font-mono">{t.stationeryIssuance.worker?.employeeId}</p>
                              </>
                            : <span className="text-xs text-gray-500">Un-issued stock</span>}
                        </td>
                        <td className="font-medium text-sm">{t.item?.name}</td>
                        <td className="font-semibold">{t.quantity ?? t.stationeryIssuance?.quantity ?? 1}</td>
                        <td>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                            t.status === 'UNDER_MAINTENANCE' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {t.status === 'UNDER_MAINTENANCE' ? 'Sent for Maintenance' : 'Needs Repair'}
                          </span>
                        </td>
                        <td className="text-gray-400 text-xs">{new Date(t.markedAt).toLocaleDateString('en-GB')}</td>
                        <td>
                          {hasPermission('repair_management') && (
                            <div className="flex items-center gap-3 flex-wrap">
                              {t.status === 'PENDING_REPAIR' && (
                                <button
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                                  onClick={() => handleSendForMaintenance(t.id)}
                                >
                                  Send for Maintenance
                                </button>
                              )}
                              {t.status === 'UNDER_MAINTENANCE' && (
                                <button
                                  className="text-green-600 hover:text-green-800 text-sm font-medium hover:underline"
                                  onClick={() => handleReturnFromMaintenance(t.id)}
                                >
                                  ✓ Returned from Maintenance
                                </button>
                              )}
                              <span className="text-gray-200">|</span>
                              <button
                                className="text-red-500 hover:text-red-700 text-sm font-medium hover:underline"
                                onClick={() => handleScrapTicket(t.id, t.item?.name || 'this item')}
                              >
                                ✕ Beyond Recovery
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {openStatRepairTickets.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-gray-400">No items currently in the repair pipeline</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          IT TAB — isolated third item pillar (itApi), mirrors the Stationery
          tab above exactly. Defense-in-depth: also re-checks canViewIT here
          even though activeTab can only reach 'it' via the it_view-gated tab
          button above (see the Tab bar).
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'it' && canViewIT && (
        <>
          <SectionHeader title="Stock Levels" />
          <div className="table-container mb-6">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  {itShowSiteCol && <th>Site</th>}
                  <th>In Stock</th>
                  <th>Issued</th>
                  {itCategoryFilter === 'DURABLE' && <th>Needs Repair</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {itInvFiltered.map((iinv) => {
                  const issued     = (itCategoryFilter === 'DURABLE' ? itDurableIssuances : itConsumableIssuances)
                    .filter((i) => i.itemId === iinv.itemId).reduce((s, i) => s + i.quantity, 0);
                  const needsRepair = needsRepairByItItemId[iinv.itemId] || 0;
                  return (
                    <tr key={iinv.id}>
                      <td>
                        {iinv.item?.requiresMaterialId ? (
                          <button
                            className="font-medium text-blue-600 hover:underline text-left"
                            onClick={() => openManageIds({ itItemId: iinv.itemId, siteId: iinv.siteId, name: iinv.item.name, aggQty: iinv.quantity })}
                            title="View Material ID units"
                          >
                            {iinv.item?.name}
                          </button>
                        ) : (
                          <p className="font-medium">{iinv.item?.name}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {iinv.item?.unit}
                          {iinv.item?.price ? ` · ₹${parseFloat(iinv.item.price).toFixed(0)}` : ''}
                        </p>
                      </td>
                      {itShowSiteCol && <td className="text-xs text-gray-500">{iinv.site?.name || '—'}</td>}
                      <td>
                        <button
                          className={`font-semibold text-lg hover:underline ${iinv.quantity <= 0 ? 'text-red-600' : 'text-gray-900'}`}
                          onClick={() => openStockHistory('IT', iinv)}
                          title="View stock addition history"
                        >
                          {iinv.quantity}
                        </button>
                      </td>
                      <td>
                        {issued > 0 ? (
                          <button
                            className="font-semibold text-indigo-600 hover:underline"
                            onClick={() => openItIssuedHistory(iinv)}
                          >
                            {issued}
                          </button>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      {itCategoryFilter === 'DURABLE' && (
                        <td>
                          {needsRepair > 0 ? (
                            <button
                              className="font-medium text-orange-500 hover:underline"
                              onClick={() => openStatusDetail(
                                `Needs Repair — ${iinv.item?.name}`,
                                repairTickets.filter((t) => t.itItemId === iinv.itemId && t.status === 'PENDING_REPAIR')
                              )}
                            >
                              {needsRepair}
                            </button>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      )}
                      <td>
                        <div className="flex gap-2 flex-wrap">
                          {itCategoryFilter === 'DURABLE' && hasPermission('it_repair_management') && (
                            <button
                              className="text-orange-500 hover:underline text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                              disabled={iinv.quantity <= 0}
                              onClick={() => openRepairFlag({ kind: 'INVENTORY', target: iinv, itemType: 'IT' })}
                            >
                              Mark as Needs Repair
                            </button>
                          )}
                          {hasPermission('it_edit') && (
                            <button
                              className="text-blue-600 hover:underline text-sm"
                              onClick={() => { setAddItStockForm({ itemId: iinv.itemId, quantity: iinv.quantity }); setEditItInvId(iinv.id); setAddItStockError(''); setShowAddItStock(true); }}
                            >
                              Edit
                            </button>
                          )}
                          {iinv.item?.requiresMaterialId && hasPermission('it_edit') && (
                            <button
                              className="text-purple-600 hover:underline text-sm"
                              onClick={() => openManageIds({ itItemId: iinv.itemId, siteId: iinv.siteId, name: iinv.item.name, aggQty: iinv.quantity })}
                            >
                              Manage IDs
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {itInvFiltered.length === 0 && (
                  <tr><td colSpan={(itShowSiteCol ? 1 : 0) + 6} className="text-center py-8 text-gray-400">
                    No IT inventory at this site
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {itCategoryFilter === 'DURABLE' && (
            <>
              <SectionHeader title="Repair & Maintenance Pipeline" />
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Marked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openItRepairTickets.map((t) => (
                      <tr key={t.id}>
                        <td>
                          {t.itIssuance
                            ? <>
                                <p className="font-medium text-sm">{t.itIssuance.worker?.name}</p>
                                <p className="text-xs text-gray-400 font-mono">{t.itIssuance.worker?.employeeId}</p>
                              </>
                            : <span className="text-xs text-gray-500">Un-issued stock</span>}
                        </td>
                        <td className="font-medium text-sm">{t.itItem?.name}</td>
                        <td className="font-semibold">{t.quantity ?? t.itIssuance?.quantity ?? 1}</td>
                        <td>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                            t.status === 'UNDER_MAINTENANCE' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {t.status === 'UNDER_MAINTENANCE' ? 'Sent for Maintenance' : 'Needs Repair'}
                          </span>
                        </td>
                        <td className="text-gray-400 text-xs">{new Date(t.markedAt).toLocaleDateString('en-GB')}</td>
                        <td>
                          {hasPermission('it_repair_management') && (
                            <div className="flex items-center gap-3 flex-wrap">
                              {t.status === 'PENDING_REPAIR' && (
                                <button
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                                  onClick={() => handleSendForMaintenance(t.id)}
                                >
                                  Send for Maintenance
                                </button>
                              )}
                              {t.status === 'UNDER_MAINTENANCE' && (
                                <button
                                  className="text-green-600 hover:text-green-800 text-sm font-medium hover:underline"
                                  onClick={() => handleReturnFromMaintenance(t.id)}
                                >
                                  ✓ Returned from Maintenance
                                </button>
                              )}
                              <span className="text-gray-200">|</span>
                              <button
                                className="text-red-500 hover:text-red-700 text-sm font-medium hover:underline"
                                onClick={() => handleScrapTicket(t.id, t.itItem?.name || 'this item')}
                              >
                                ✕ Beyond Recovery
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {openItRepairTickets.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-gray-400">No items currently in the repair pipeline</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          WORKERS TAB — search, assign, issue, return
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'workers' && (() => {
        const searchResults  = smartSearchWorkers(allWorkers, workerSearch);
        const isSearching    = searchResults !== null;
        const assignedIds    = new Set(siteAssignments.map((a) => a.workerId));

        return (
          <>
            {/* ── Search bar ──────────────────────────────────────────────── */}
            <div className="mb-4 relative">
              <input
                type="text"
                className="input pl-9 w-52 !py-1.5 !text-sm"
                placeholder="Search by name or employee ID…"
                value={workerSearch}
                onChange={(e) => setWorkerSearch(e.target.value)}
              />
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              {workerSearch && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setWorkerSearch('')}
                >×</button>
              )}
            </div>

            {/* ── Search results ───────────────────────────────────────────── */}
            {isSearching ? (
              allWorkersLoading ? (
                <p className="text-sm text-gray-400 py-6 text-center">Loading workers…</p>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No workers match "{workerSearch}"</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Worker</th>
                        <th>Employee ID</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((w) => {
                        const atThisSite = assignedIds.has(w.id);
                        return (
                          <tr key={w.id}>
                            <td className="font-medium">{w.name}</td>
                            <td className="text-gray-500 font-mono text-sm">{w.employeeId || '—'}</td>
                            <td>
                              {workerBadge(w) ? (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${workerBadge(w).cls}`}>
                                  {workerBadge(w).label}
                                </span>
                              ) : (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  {w.employeeType || '—'}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                {atThisSite ? (
                                  <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg font-medium">
                                    At this site
                                  </span>
                                ) : null}
                                {w.workerCategory !== 'SUBCONTRACTOR' && (
                                  <button
                                    className="text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors"
                                    onClick={() => { setAssignTarget(w); setAssignSiteId(selectedSiteId ? String(selectedSiteId) : ''); setAssignError(''); }}
                                  >
                                    Assign to Site
                                  </button>
                                )}
                                {hasPermission('slip_generation') && (
                                  <button
                                    className="text-sm font-medium text-slate-600 hover:text-slate-800"
                                    onClick={() => setSlipMenuTarget(w)}
                                    title="Generate a printable material slip (A4 PDF)"
                                  >
                                    Generate Slip
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* ── Default view: workers at the selected site ──────────────── */
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Worker</th>
                      <th>Employee ID</th>
                      <th>Materials</th>
                      <th>Consumables</th>
                      <th>Durables</th>
                      {canViewIT && <th>IT</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteWorkerList.flatMap((w) => {
                      // Counts are worker-wide (every site's holdings), not just this
                      // site's stock — a Guest/transferred worker can already hold
                      // items issued elsewhere; see workerHoldingsKits/Issuances.
                      const wMaterials   = workerHoldingsKits.filter((wk) => wk.workerId === w.id);
                      const wConsumables = workerHoldingsIssuances.filter((i) => i.workerId === w.id && i.item?.category === 'CONSUMABLE');
                      const wDurables    = workerHoldingsIssuances.filter((i) => i.workerId === w.id && i.item?.category === 'DURABLE');
                      const wItItems     = workerHoldingsItIssuances.filter((i) => i.workerId === w.id);
                      // Sum outstanding quantity, not record count — a single record can
                      // represent more than one unit (e.g. a multi-unit consumable
                      // issuance), so counting records under-reports how much a worker
                      // actually holds.
                      const wMaterialsQty   = wMaterials.reduce((s, wk) => s + (wk.quantity - (wk.returnedQuantity || 0)), 0);
                      const wConsumablesQty = wConsumables.reduce((s, i) => s + i.quantity, 0);
                      const wDurablesQty    = wDurables.reduce((s, i) => s + i.quantity, 0);
                      const wItQty          = wItItems.reduce((s, i) => s + i.quantity, 0);
                      const isExpanded   = expandedWorkerId === w.id;
                      return [
                        <tr
                          key={w.id}
                          className={`cursor-pointer select-none transition-colors ${isExpanded ? 'bg-indigo-50 border-l-2 border-indigo-400' : 'hover:bg-gray-50'}`}
                          onClick={(e) => {
                            if (e.target.closest('button')) return;
                            handleExpandWorker(w);
                          }}
                        >
                          <td>
                            <div className="flex items-center gap-2">
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isExpanded ? 'bg-indigo-500' : 'bg-gray-300'}`} />
                              <span className="font-medium">{w.name}</span>
                              {workerBadge(w) && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${workerBadge(w).cls}`}>
                                  {workerBadge(w).label}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-gray-500 font-mono text-sm">{w.employeeId}</td>
                          <td>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wMaterialsQty > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
                              {wMaterialsQty}
                            </span>
                          </td>
                          <td>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wConsumablesQty > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                              {wConsumablesQty}
                            </span>
                          </td>
                          <td>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wDurablesQty > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>
                              {wDurablesQty}
                            </span>
                          </td>
                          {canViewIT && (
                            <td>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wItQty > 0 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-400'}`}>
                                {wItQty}
                              </span>
                            </td>
                          )}
                          <td>
                            <div className="flex items-center gap-2">
                              <button
                                className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors"
                                onClick={(e) => { e.stopPropagation(); openIssue(w); }}
                              >
                                + Issue
                              </button>
                              {hasPermission('slip_generation') && (
                                <button
                                  className="text-slate-600 hover:text-slate-800 text-sm font-medium"
                                  onClick={(e) => { e.stopPropagation(); setSlipMenuTarget(w); }}
                                  title="Generate a printable material slip (A4 PDF)"
                                >
                                  Generate Slip
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>,
                        isExpanded && (
                          <tr key={`${w.id}-detail`}>
                            <td colSpan={canViewIT ? 7 : 6} className="p-0 bg-slate-50 border-b-2 border-indigo-100">
                              <WorkerDetailPanel
                                kits={workerKitDetail}
                                consumables={workerStatDetail.filter((i) => i.item?.category === 'CONSUMABLE')}
                                durables={workerStatDetail.filter((i) => i.item?.category === 'DURABLE')}
                                loading={loadingDetail}
                                onReturnClick={({ kind, target, outstanding }) => {
                                  setReturnChoiceTarget({ kind, target, outstanding });
                                  setError('');
                                }}
                                pendingDamageWorkerKitIds={pendingDamageWorkerKitIds}
                                pendingDamageStationeryIssuanceIds={pendingDamageStationeryIssuanceIds}
                                repairStatusByStationeryIssuanceId={repairStatusByStationeryIssuanceId}
                                onMarkRepairedClick={openRepairFlag}
                                canRepair={hasPermission('repair_management')}
                                onViewMaterialHistory={openMaterialHistory}
                                // IT — only passed when the acting user holds it_view; a user
                                // without it never receives these props, so the IT column
                                // never renders for them (see showIT in WorkerDetailPanel).
                                itConsumables={canViewIT ? workerItDetail.filter((i) => i.item?.category === 'CONSUMABLE') : undefined}
                                itDurables={canViewIT ? workerItDetail.filter((i) => i.item?.category === 'DURABLE') : undefined}
                                repairStatusByItIssuanceId={repairStatusByItIssuanceId}
                                canRepairIT={hasPermission('it_repair_management')}
                              />
                            </td>
                          </tr>
                        ),
                      ].filter(Boolean);
                    })}
                    {siteWorkerList.length === 0 && (
                      <tr><td colSpan={canViewIT ? 7 : 6} className="text-center py-10 text-gray-400">
                        No workers assigned to this site. Search above to find and assign workers.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════════
          REQUESTS TAB — employee-initiated Material/Stationery requests.
          Distinct from Requirement (site-vs-central-store procurement) —
          this is "I need this item", optional, and never blocks the normal
          issue flow (see the auto-close hook in workerKits.js/stationery.js).
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'requests' && (
        requestsLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading requests…</p>
        ) : (() => {
          const filteredRequests = requestsKindFilter === 'ALL'
            ? requests
            : requests.filter((r) => r.itemKind === requestsKindFilter);
          return (
          <div>
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit mb-4">
              {[
                ['ALL', 'All'],
                ['MATERIAL', 'Materials'],
                ['STATIONERY', 'Stationery'],
                ...(canViewIT ? [['IT', 'IT']] : []),
              ].map(([v, l]) => (
                <button
                  key={v} type="button"
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${requestsKindFilter === v ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500'}`}
                  onClick={() => setRequestsKindFilter(v)}
                >
                  {l}
                </button>
              ))}
            </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th>Reply</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <p className="font-medium text-sm">{r.worker?.name}</p>
                      <p className="text-xs text-gray-400 font-mono">{r.worker?.employeeId || '—'}</p>
                    </td>
                    <td className="font-medium text-sm">{r.kit?.name || r.stationeryItem?.name || r.itItem?.name || '—'}</td>
                    <td className="text-gray-500 text-sm">{r.itemKind === 'MATERIAL' ? 'Material' : r.itemKind === 'STATIONERY' ? 'Stationery' : 'IT'}</td>
                    <td className="font-semibold">{r.quantity}</td>
                    <td className="text-gray-400 text-xs">{new Date(r.requestedAt).toLocaleDateString('en-GB')}</td>
                    <td>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        r.status === 'pending' ? 'bg-amber-100 text-amber-700'
                        : r.status === 'fulfilled' ? 'bg-emerald-100 text-emerald-700'
                        : r.status === 'closed' ? 'bg-rose-100 text-rose-700'
                        : 'bg-slate-100 text-slate-500'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="max-w-[200px]">
                      {r.replyMessage ? (
                        <p className="text-xs text-gray-600 truncate" title={r.replyMessage}>{r.replyMessage}</p>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2 flex-wrap">
                        {r.status === 'pending' && (r.itemKind !== 'IT' || hasPermission('it_issue')) && (
                          <button
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-colors"
                            onClick={() => openIssueRequest(r)}
                          >
                            Issue
                          </button>
                        )}
                        {r.status === 'pending' && (
                          <button
                            className="text-rose-600 hover:text-rose-800 text-sm font-semibold bg-rose-50 hover:bg-rose-100 px-3 py-1 rounded-lg transition-colors"
                            onClick={() => openClose(r)}
                          >
                            Close
                          </button>
                        )}
                        <button
                          className="text-gray-600 hover:text-gray-800 text-sm font-medium hover:underline"
                          onClick={() => openReply(r)}
                        >
                          {r.replyMessage ? 'Edit Reply' : 'Reply'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRequests.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-gray-400">
                    {requests.length === 0 ? 'No requests yet.' : 'No requests match this filter.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
          );
        })()
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          DAMAGE APPROVAL TAB — items flagged Damaged sit here until a
          damage_approval holder reviews them; the underlying item is NOT
          marked Damaged until approved (separation of duties from
          damage_flagging, no self-approval).
      ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'damage-approval' && (
        damageReportsLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading damage reports…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {damageReports.map((r) => {
              const itemName = r.workerKit?.kit?.name || r.stationeryIssuance?.item?.name || r.inventory?.kit?.name || 'Item';
              const worker   = r.workerKit?.worker || r.stationeryIssuance?.worker;
              return (
                <div key={r.id} className="border border-gray-200 rounded-xl p-4 bg-white">
                  <p className="font-semibold text-sm text-gray-900">{itemName}</p>
                  {worker ? (
                    <p className="text-xs text-gray-400 mt-0.5">{worker.name} {worker.employeeId ? `· ${worker.employeeId}` : ''}</p>
                  ) : r.inventory ? (
                    <p className="text-xs text-gray-400 mt-0.5">{r.inventory.site?.name} (Inventory) · Qty: {r.quantity}</p>
                  ) : null}
                  <p className="text-xs text-gray-400 mt-0.5">Flagged by {r.flaggedByUsername} · {new Date(r.flaggedAt).toLocaleDateString('en-GB')}</p>
                  {r.remarks && <p className="text-xs text-gray-500 mt-2 italic">"{r.remarks}"</p>}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {r.photoUrls.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt="Damage evidence" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                      </a>
                    ))}
                    {r.photoUrls.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No photos attached</p>
                    )}
                  </div>
                  {hasPermission('damage_flagging') && (
                    <label className="inline-block mt-2 text-xs text-indigo-600 hover:underline cursor-pointer">
                      + Attach Photo
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleAddDamagePhotos(r.id, Array.from(e.target.files || []))}
                      />
                    </label>
                  )}
                  <div className="flex gap-2 mt-4">
                    {hasPermission('damage_approval') && (
                      <>
                        <button className="btn-primary flex-1" onClick={() => handleApproveDamage(r.id)}>Approve</button>
                        <button className="btn-secondary flex-1" onClick={() => { setRejectTarget(r); setRejectRemarks(''); }}>Reject</button>
                      </>
                    )}
                    {hasPermission('damage_flagging') && (
                      <button className="btn-secondary flex-1" onClick={() => handleWithdrawDamage(r.id, itemName)}>Withdraw</button>
                    )}
                  </div>
                </div>
              );
            })}
            {damageReports.length === 0 && (
              <p className="col-span-full text-center py-10 text-gray-400">No pending damage reports.</p>
            )}
          </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          MARK DAMAGED MODAL — upload photo(s), submit for approval
      ════════════════════════════════════════════════════════════════════════ */}
      {damageFlagTarget && (
        <Modal
          title="Report Item as Damaged"
          subtitle={damageFlagTarget.target.kit?.name}
          onClose={() => setDamageFlagTarget(null)}
        >
          <form onSubmit={handleFlagDamagedSubmit} className="space-y-4">
            <p className="text-sm text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              This item will move to the Pending Damage Approval queue. It is only marked Damaged once approved.
            </p>
            {damageFlagTarget.kind === 'INVENTORY' && (() => {
              const target = damageFlagTarget.target;
              const available = Math.max(0, (target.quantity || 0) - (target.damagedQuantity || 0));
              const needsUnitPicker = target.kit?.requiresMaterialId;
              const toggleUnit = (id) => setDamageFlagUnitIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length < damageFlagQuantity ? [...prev, id] : prev));
              return (
                <>
                  <div>
                    <label className="label">Quantity Being Reported ({available} available)</label>
                    <input
                      className="input"
                      type="number"
                      min="1"
                      max={available}
                      value={damageFlagQuantity}
                      onChange={(e) => { setDamageFlagQuantity(parseInt(e.target.value, 10) || 1); setDamageFlagUnitIds([]); }}
                      required
                    />
                  </div>
                  {needsUnitPicker && (
                    <div>
                      <label className="label">Select {damageFlagQuantity} Material ID(s)</label>
                      <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                        {damageFlagAvailableUnits.length === 0 && (
                          <p className="text-sm text-gray-400 p-3">No in-stock units available to select.</p>
                        )}
                        {damageFlagAvailableUnits.map((u) => (
                          <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={damageFlagUnitIds.includes(u.id)} onChange={() => toggleUnit(u.id)} />
                            <span className="font-mono">{u.materialIdCode}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{damageFlagUnitIds.length} of {damageFlagQuantity} selected</p>
                    </div>
                  )}
                </>
              );
            })()}
            <div>
              <label className="label">Photos <span className="text-gray-400 font-normal">(optional — up to 5, image files only; can also be added later while pending)</span></label>
              <input
                type="file"
                accept="image/*"
                multiple
                className="input"
                onChange={(e) => setDamageFlagFiles(Array.from(e.target.files || []).slice(0, 5))}
              />
            </div>
            <div>
              <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                className="input"
                rows={3}
                value={damageFlagRemarks}
                onChange={(e) => setDamageFlagRemarks(e.target.value)}
              />
            </div>
            {damageFlagError && <p className="text-red-600 text-sm">{damageFlagError}</p>}
            <ModalActions
              onCancel={() => setDamageFlagTarget(null)}
              submitLabel={damageFlagSaving ? 'Submitting…' : 'Submit for Approval'}
              disabled={damageFlagSaving
                || (damageFlagTarget.kind === 'INVENTORY' && damageFlagQuantity < 1)
                || (damageFlagTarget.kind === 'INVENTORY' && damageFlagTarget.target.kit?.requiresMaterialId
                  && damageFlagUnitIds.length !== damageFlagQuantity)}
            />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          MARK AS NEEDS REPAIR MODAL (Stationery Durables — Repair & Maintenance
          workflow, Step 1) — quantity + Material ID picker only needed for
          un-issued stock; an issued item's full quantity is implied.
      ════════════════════════════════════════════════════════════════════════ */}
      {repairFlagTarget && (
        <Modal
          title="Mark as Needs Repair"
          subtitle={repairFlagTarget.target.item?.name}
          onClose={() => setRepairFlagTarget(null)}
        >
          <form onSubmit={handleRepairFlagSubmit} className="space-y-4">
            <p className="text-sm text-gray-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              This item will move to the Repair & Maintenance pipeline with status "Needs Repair". From there it can be sent for maintenance and, once complete, returned to available stock as "Repaired".
            </p>
            {repairFlagTarget.kind === 'INVENTORY' && (() => {
              const target = repairFlagTarget.target;
              const needsUnitPicker = target.item?.requiresMaterialId;
              const toggleUnit = (id) => setRepairFlagUnitIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length < repairFlagQuantity ? [...prev, id] : prev));
              return (
                <>
                  <div>
                    <label className="label">Quantity ({target.quantity} available)</label>
                    <input
                      className="input"
                      type="number"
                      min="1"
                      max={target.quantity}
                      value={repairFlagQuantity}
                      onChange={(e) => { setRepairFlagQuantity(parseInt(e.target.value, 10) || 1); setRepairFlagUnitIds([]); }}
                      required
                    />
                  </div>
                  {needsUnitPicker && (
                    <div>
                      <label className="label">Select {repairFlagQuantity} Material ID(s)</label>
                      <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                        {repairFlagAvailableUnits.length === 0 && (
                          <p className="text-sm text-gray-400 p-3">No in-stock units available to select.</p>
                        )}
                        {repairFlagAvailableUnits.map((u) => (
                          <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={repairFlagUnitIds.includes(u.id)} onChange={() => toggleUnit(u.id)} />
                            <span className="font-mono">{u.materialIdCode}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{repairFlagUnitIds.length} of {repairFlagQuantity} selected</p>
                    </div>
                  )}
                </>
              );
            })()}
            {repairFlagError && <p className="text-red-600 text-sm">{repairFlagError}</p>}
            <ModalActions
              onCancel={() => setRepairFlagTarget(null)}
              submitLabel={repairFlagSaving ? 'Saving…' : 'Mark as Needs Repair'}
              disabled={repairFlagSaving
                || (repairFlagTarget.kind === 'INVENTORY' && repairFlagQuantity < 1)
                || (repairFlagTarget.kind === 'INVENTORY' && repairFlagTarget.target.item?.requiresMaterialId
                  && repairFlagUnitIds.length !== repairFlagQuantity)}
            />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          STATUS DETAIL MODAL — "Needs Repair" / "Scrapped" click-through from
          the Durables Stock Levels table. Each row is one RepairTicket; a
          Material ID (when the item requires one) is clickable and opens the
          full per-asset lifecycle history.
      ════════════════════════════════════════════════════════════════════════ */}
      {statusDetailModal && (
        <Modal
          title={statusDetailModal.title}
          onClose={() => setStatusDetailModal(null)}
          wide
        >
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Material Name</th>
                  <th>Material ID</th>
                  <th>Site</th>
                  <th>Current Holder</th>
                  <th>Date Marked</th>
                  <th>Current Status</th>
                </tr>
              </thead>
              <tbody>
                {statusDetailModal.tickets.map((t) => {
                  const codes = (t.materialUnitIds || []).map((id) => statusDetailCodes[id]?.materialIdCode).filter(Boolean);
                  const statusLabel = { PENDING_REPAIR: 'Needs Repair', UNDER_MAINTENANCE: 'Sent for Maintenance', REPAIRED: 'Repaired', SCRAPPED: 'Beyond Recovery (Scrapped)' }[t.status] || t.status;
                  return (
                    <tr key={t.id}>
                      <td className="font-medium text-sm">{t.item?.name || t.itItem?.name}</td>
                      <td>
                        {codes.length > 0 ? codes.map((code, i) => {
                          const unitId = t.materialUnitIds[i];
                          return (
                            <button
                              key={unitId}
                              className="font-mono text-xs text-indigo-600 hover:underline mr-1"
                              onClick={() => openMaterialHistory(unitId)}
                            >
                              {code}
                            </button>
                          );
                        }) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="text-sm">{t.site?.name || '—'}</td>
                      <td className="text-sm">
                        {t.stationeryIssuance || t.itIssuance
                          ? (() => { const worker = (t.stationeryIssuance || t.itIssuance).worker; return <>{worker?.name}{worker?.employeeId ? ` (${worker.employeeId})` : ''}</>; })()
                          : <span className="text-gray-400">Un-issued stock</span>}
                      </td>
                      <td className="text-gray-400 text-xs">{new Date(t.markedAt).toLocaleDateString('en-GB')}</td>
                      <td>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200">
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {statusDetailModal.tickets.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-400">No items to show</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          MATERIAL ID HISTORY MODAL — full per-asset lifecycle log for one
          serialized unit (issue/return/repair-pipeline/damage events).
      ════════════════════════════════════════════════════════════════════════ */}
      {historyModal && (() => {
        const canRenameHere = historyModal !== 'loading' && (historyModal.unit?.kitId ? hasPermission('inventory_edit') : historyModal.unit?.itItemId ? hasPermission('it_edit') : hasPermission('stationery_edit'));
        return (
        <Modal
          title="Material Details"
          subtitle={historyModal !== 'loading' ? historyModal.unit?.materialIdCode : undefined}
          onClose={() => { setHistoryModal(null); setDetailBackTarget(null); }}
          zClass="z-[60]"
        >
          {historyModal === 'loading' ? (
            <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-3">
              {detailBackTarget && (
                <button
                  className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1"
                  onClick={() => { const backTo = detailBackTarget; setHistoryModal(null); setDetailBackTarget(null); openManageIds(backTo); }}
                >
                  ← Back to list
                </button>
              )}
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-500">
                  {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name || historyModal.unit?.itItem?.name} · {historyModal.unit?.site?.name}
                </p>
                {canRenameHere && !detailRenaming && (
                  <button
                    className="text-blue-600 hover:underline text-sm flex-shrink-0"
                    onClick={() => { setDetailRenaming(true); setDetailRenameValue(historyModal.unit.materialIdCode); setDetailRenameError(''); }}
                  >Rename</button>
                )}
              </div>
              {detailRenaming && (
                <div className="flex items-center gap-2">
                  <input
                    className="input flex-1 py-1"
                    value={detailRenameValue}
                    onChange={(e) => setDetailRenameValue(e.target.value)}
                    autoFocus
                  />
                  <button className="text-blue-600 hover:underline text-sm" disabled={detailRenameSaving} onClick={submitDetailRename}>Save</button>
                  <button className="text-gray-500 hover:underline text-sm" onClick={() => setDetailRenaming(false)}>Cancel</button>
                </div>
              )}
              {detailRenameError && <p className="text-red-600 text-sm">{detailRenameError}</p>}

              <MaterialInfoPanel materialUnitId={historyModal.unit?.id} unitKitId={historyModal.unit?.kitId} unitItItemId={historyModal.unit?.itItemId} />

              <div className="pt-3 border-t border-gray-100">
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  onClick={() => setHistoryExpanded((v) => !v)}
                >
                  <span className={`transition-transform ${historyExpanded ? 'rotate-90' : ''}`}>▸</span>
                  Item History ({historyModal.events.length}) — click to {historyExpanded ? 'collapse' : 'expand'}
                </button>
                {historyExpanded && (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto mt-2">
                    {historyModal.events.map((e) => (
                      <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                          {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                          {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                        </div>
                        <p className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(e.occurredAt).toLocaleString('en-GB')}
                        </p>
                      </div>
                    ))}
                    {historyModal.events.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════════
          REJECT DAMAGE REPORT MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {rejectTarget && (
        <Modal
          title="Reject Damage Report"
          subtitle={rejectTarget.workerKit?.kit?.name || rejectTarget.stationeryIssuance?.item?.name || rejectTarget.inventory?.kit?.name}
          onClose={() => { setRejectTarget(null); setRejectError(''); }}
        >
          <form onSubmit={handleRejectDamageSubmit} className="space-y-4">
            <div>
              <label className="label">Reason <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                className="input"
                rows={3}
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
              />
            </div>
            {rejectError && <p className="text-red-600 text-sm">{rejectError}</p>}
            <ModalActions onCancel={() => { setRejectTarget(null); setRejectError(''); }} submitLabel="Reject" />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        open={!!withdrawDamageTarget}
        title="Withdraw Damage Report"
        message={withdrawDamageTarget ? `Withdraw the damage report for "${withdrawDamageTarget.itemName}"? This restores it to available stock and cannot be undone.` : ''}
        confirmLabel="Withdraw"
        onConfirm={submitWithdrawDamage}
        onCancel={() => setWithdrawDamageTarget(null)}
      />

      <ConfirmDialog
        open={!!scrapTicketTarget}
        title="Mark as Beyond Recovery"
        message={scrapTicketTarget ? `Mark "${scrapTicketTarget.itemName}" as Beyond Recovery? This means the item is scrapped and cannot be returned to inventory. This action is permanent and will be recorded for audit purposes.` : ''}
        confirmLabel="Mark Scrapped"
        onConfirm={submitScrapTicket}
        onCancel={() => setScrapTicketTarget(null)}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          GENERATE SLIP MENU — choose whether Paid Items are included
      ════════════════════════════════════════════════════════════════════════ */}
      {slipMenuTarget && (
        <Modal
          title="Generate Slip"
          subtitle={slipMenuTarget.name}
          onClose={() => setSlipMenuTarget(null)}
        >
          <div className="space-y-3">
            <button className="btn-primary w-full" autoFocus onClick={() => handleGenerateSlip(slipMenuTarget, false)}>
              Generate Slip (Excluding Paid Items)
            </button>
            <button className="btn-secondary w-full" onClick={() => handleGenerateSlip(slipMenuTarget, true)}>
              Generate Slip (Including Paid Items)
            </button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          ISSUE FROM REQUEST MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {issueRequestTarget && (
        <Modal
          title={`Issue "${issueRequestTarget.kit?.name || issueRequestTarget.stationeryItem?.name || issueRequestTarget.itItem?.name}"`}
          subtitle={`To: ${issueRequestTarget.worker?.name}`}
          onClose={() => setIssueRequestTarget(null)}
        >
          <form onSubmit={handleIssueRequestSubmit} className="space-y-4">
            {issueRequestTarget.itemKind === 'MATERIAL' ? (
              <div>
                <label className="label">Inventory Batch (at {selectedSite?.name || 'selected site'}) *</label>
                <select className="input" value={issueRequestInventoryId} onChange={(e) => handleIssueRequestInventoryChange(e.target.value)} required>
                  <option value="">Select batch…</option>
                  {inventory.filter((r) => r.kitId === issueRequestTarget.kitId && (r.quantity - (r.damagedQuantity || 0)) > 0).map((r) => (
                    <option key={r.id} value={r.id}>Available: {r.quantity - (r.damagedQuantity || 0)}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Switch to the worker's site above if no batch is listed here.</p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                Will be issued from <strong>{selectedSite?.name || 'the selected site'}</strong>'s {issueRequestTarget.itemKind === 'STATIONERY' ? 'stationery' : 'IT'} stock.
              </p>
            )}
            {((issueRequestTarget.itemKind === 'MATERIAL' && issueRequestTarget.kit?.requiresMaterialId && issueRequestInventoryId)
              || (issueRequestTarget.itemKind === 'STATIONERY' && issueRequestTarget.stationeryItem?.requiresMaterialId)
              || (issueRequestTarget.itemKind === 'IT' && issueRequestTarget.itItem?.requiresMaterialId)) && (
              <MaterialIdPicker
                value={issueRequestMaterialUnitId}
                onChange={setIssueRequestMaterialUnitId}
                units={issueRequestMaterialUnits}
                kitId={issueRequestTarget.itemKind === 'MATERIAL' ? issueRequestTarget.kitId : undefined}
                stationeryItemId={issueRequestTarget.itemKind === 'STATIONERY' ? issueRequestTarget.stationeryItemId : undefined}
                itItemId={issueRequestTarget.itemKind === 'IT' ? issueRequestTarget.itItemId : undefined}
                siteId={selectedSiteId}
                onUnitAdded={(unit) => setIssueRequestMaterialUnits((prev) => [...prev, unit])}
              />
            )}
            {issueRequestValidityWarn && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                <p className="font-semibold text-amber-800 mb-1">⚠ Item Still Within Validity Period</p>
                <p className="text-amber-700">{issueRequestValidityWarn.message}</p>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={issueRequestConfirmDeduction}
                    onChange={(e) => setIssueRequestConfirmDeduction(e.target.checked)}
                    className="w-4 h-4 accent-amber-600"
                  />
                  <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                </label>
              </div>
            )}
            {issueRequestMaxQtyWarn && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                <p className="font-semibold text-amber-800 mb-1">⚠ Maximum Quantity Reached</p>
                <p className="text-amber-700">{issueRequestMaxQtyWarn.message}</p>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={issueRequestConfirmMaxQtyDeduction}
                    onChange={(e) => setIssueRequestConfirmMaxQtyDeduction(e.target.checked)}
                    className="w-4 h-4 accent-amber-600"
                  />
                  <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                </label>
              </div>
            )}
            {issueRequestError && <p className="text-red-600 text-sm">{issueRequestError}</p>}
            <ModalActions
              onCancel={() => setIssueRequestTarget(null)}
              submitLabel={issueRequestSaving ? 'Issuing…' : 'Issue'}
              disabled={
                issueRequestSaving
                || (issueRequestTarget.itemKind === 'MATERIAL' && !issueRequestInventoryId)
                || (!!issueRequestValidityWarn && !issueRequestConfirmDeduction)
                || (!!issueRequestMaxQtyWarn && !issueRequestConfirmMaxQtyDeduction)
                || (issueRequestTarget.itemKind === 'MATERIAL' && issueRequestTarget.kit?.requiresMaterialId && !issueRequestMaterialUnitId)
                || (issueRequestTarget.itemKind === 'STATIONERY' && issueRequestTarget.stationeryItem?.requiresMaterialId && !issueRequestMaterialUnitId)
                || (issueRequestTarget.itemKind === 'IT' && issueRequestTarget.itItem?.requiresMaterialId && !issueRequestMaterialUnitId)
              }
            />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          REPLY TO REQUEST MODAL — a store-side note the employee sees on
          their own My Requests panel. Independent of status/Issue.
      ════════════════════════════════════════════════════════════════════════ */}
      {replyTarget && (
        <Modal
          title="Reply to Request"
          subtitle={`${replyTarget.worker?.name} — ${replyTarget.kit?.name || replyTarget.stationeryItem?.name || replyTarget.itItem?.name}`}
          onClose={() => setReplyTarget(null)}
        >
          <form onSubmit={handleReplySubmit} className="space-y-4">
            <div>
              <label className="label">Message</label>
              <textarea
                className="input"
                rows={4}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="e.g. Out of stock, ordering more — will issue by Friday."
                required
              />
            </div>
            {replyError && <p className="text-red-600 text-sm">{replyError}</p>}
            <ModalActions onCancel={() => setReplyTarget(null)} submitLabel={replySaving ? 'Sending…' : 'Send Reply'} disabled={replySaving} />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          CLOSE REQUEST MODAL — closes a pending request without fulfilling it
          (permanently out of stock, duplicate, not approved, etc.), with an
          optional reply the requester sees the same way a normal Reply does.
      ════════════════════════════════════════════════════════════════════════ */}
      {closeTarget && (
        <Modal
          title="Close Request"
          subtitle={`${closeTarget.worker?.name} — ${closeTarget.kit?.name || closeTarget.stationeryItem?.name || closeTarget.itItem?.name}`}
          onClose={() => setCloseTarget(null)}
        >
          <form onSubmit={handleCloseSubmit} className="space-y-4">
            <p className="text-sm text-gray-500">
              This closes the request without issuing anything — use this when it will never be fulfilled (out of stock, duplicate, not approved, etc.). The requester can no longer act on it.
            </p>
            <div>
              <label className="label">Reply <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                className="input"
                rows={4}
                value={closeMessage}
                onChange={(e) => setCloseMessage(e.target.value)}
                placeholder="e.g. This item has been discontinued and won't be restocked."
              />
            </div>
            {closeError && <p className="text-red-600 text-sm">{closeError}</p>}
            <ModalActions onCancel={() => setCloseTarget(null)} submitLabel={closeSaving ? 'Closing…' : 'Close Request'} disabled={closeSaving} />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          ASSIGN WORKER TO SITE MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {assignTarget && (
        <Modal
          title="Assign Worker to Site"
          subtitle={`${assignTarget.name}${assignTarget.employeeId ? ` · ${assignTarget.employeeId}` : ''}`}
          onClose={() => { setAssignTarget(null); setAssignError(''); }}
        >
          <form onSubmit={handleAssignWorker} className="space-y-4">
            <div>
              <label className="label">Site *</label>
              <SearchableSelect
                options={sites.map((s) => ({ id: s.id, label: s.name }))}
                value={assignSiteId}
                onChange={setAssignSiteId}
                placeholder="Select a site…"
                required
              />
            </div>
            {assignError && <p className="text-red-600 text-sm">{assignError}</p>}
            <ModalActions
              onCancel={() => { setAssignTarget(null); setAssignError(''); }}
              submitLabel="Assign"
            />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          ADD GUEST MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {showAddGuest && (
        <Modal
          title="Add Guest"
          subtitle="Guests support the same issue/return workflow as regular workers"
          onClose={() => { setShowAddGuest(false); setAddGuestError(''); setAddGuestFoundWorker(null); setAddGuestShowSuggestions(false); }}
        >
          <form onSubmit={handleAddGuestSubmit} className="space-y-4">
            <div className="relative">
              <label className="label">Employee ID <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                className="input"
                value={addGuestForm.employeeId}
                onChange={(e) => { setAddGuestForm({ ...addGuestForm, employeeId: e.target.value }); setAddGuestFoundWorker(null); setAddGuestShowSuggestions(true); }}
                onFocus={() => setAddGuestShowSuggestions(true)}
                onBlur={() => {
                  // Delayed so a click on a suggestion (which blurs the input
                  // first) still registers before the list disappears.
                  setTimeout(() => setAddGuestShowSuggestions(false), 150);
                  handleAddGuestEmployeeIdBlur();
                }}
                placeholder="Type to search by ID or name…"
                autoComplete="off"
              />
              {addGuestShowSuggestions && addGuestSuggestions.length > 0 && (
                <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {addGuestSuggestions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between gap-2"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handlePickAddGuestSuggestion(s)}
                      >
                        <span>
                          <span className="font-medium text-gray-900">{s.name}</span>{' '}
                          <span className="text-gray-400 font-mono text-xs">{s.employeeId}</span>
                        </span>
                        {s.workerCategory && s.workerCategory !== 'STANDARD' && (
                          <span className="text-xs text-gray-400">{s.workerCategory === 'GUEST' ? 'Guest' : 'Subcontractor'}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {addGuestChecking && <p className="text-xs text-gray-400 mt-1">Checking…</p>}
            </div>
            {addGuestFoundWorker && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>{addGuestFoundWorker.name}</strong> already exists
                {addGuestFoundWorker.siteAssignments?.length > 0 && (
                  <> — assigned to {addGuestFoundWorker.siteAssignments.map((a) => a.site?.name).filter(Boolean).join(', ')}</>
                )}
                . They'll be added as a Guest here without changing their home site assignment, and all their existing history stays on their one record.
                {(addGuestFoundWorker.kits?.length > 0 || addGuestFoundWorker.stationeryIssuances?.length > 0) && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="font-medium mb-1">Items currently with them:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {addGuestFoundWorker.kits?.map((k) => (
                        <li key={`kit-${k.id}`}>
                          {k.kit?.name} × {k.quantity - k.returnedQuantity} <span className="text-blue-500">({k.inventory?.site?.name || '—'})</span>
                        </li>
                      ))}
                      {addGuestFoundWorker.stationeryIssuances?.map((s) => (
                        <li key={`stat-${s.id}`}>
                          {s.item?.name} × {s.quantity - s.returnedQty} <span className="text-blue-500">({s.site?.name || '—'})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="label">Name *</label>
              <input
                className="input"
                value={addGuestFoundWorker ? addGuestFoundWorker.name : addGuestForm.name}
                onChange={(e) => setAddGuestForm({ ...addGuestForm, name: e.target.value })}
                disabled={!!addGuestFoundWorker}
                required
              />
            </div>
            <div>
              <label className="label">Role <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                className="input"
                value={addGuestForm.role}
                onChange={(e) => setAddGuestForm({ ...addGuestForm, role: e.target.value })}
                disabled={!!addGuestFoundWorker}
              />
            </div>
            {addGuestError && <p className="text-red-600 text-sm">{addGuestError}</p>}
            <ModalActions
              onCancel={() => { setShowAddGuest(false); setAddGuestError(''); setAddGuestFoundWorker(null); }}
              submitLabel={addGuestSaving ? 'Saving…' : 'Add Guest'}
              disabled={addGuestSaving || (!addGuestFoundWorker && !addGuestForm.name.trim())}
            />
          </form>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          STATIONERY ADD STOCK MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {showAddStatStock && (
        <Modal
          title={editStatInvId ? 'Edit Stock' : `Add ${statCategoryFilter === 'CONSUMABLE' ? 'Consumable' : 'Durable'} Stock`}
          subtitle={selectedSite ? `Site: ${selectedSite.name}` : undefined}
          onClose={() => { setShowAddStatStock(false); setEditStatInvId(null); setAddStatStockError(''); setAddStatStockMaterialIds(''); }}
        >
          <form onSubmit={handleAddStatStockSubmit} className="space-y-4">
            <div>
              <label className="label">Item *</label>
              <SearchableSelect
                options={allStatItems.filter((it) => it.category === statCategoryFilter).map((it) => ({ id: it.id, label: `${it.name} · ${it.unit}` }))}
                value={addStatStockForm.itemId}
                onChange={(id) => setAddStatStockForm({ ...addStatStockForm, itemId: id })}
                placeholder="Search item…"
                disabled={!!editStatInvId}
                required
              />
            </div>
            <div>
              <label className="label">{editStatInvId ? 'Quantity *' : 'Quantity to Add *'}</label>
              <input
                className="input"
                type="number"
                min={editStatInvId ? '0' : '1'}
                value={addStatStockForm.quantity}
                onChange={(e) => setAddStatStockForm({ ...addStatStockForm, quantity: e.target.value })}
                placeholder="e.g. 50"
                required
                disabled={!!editStatInvId && addStatItem?.requiresMaterialId}
              />
              <p className="text-xs text-gray-400 mt-1">
                {editStatInvId
                  ? (addStatItem?.requiresMaterialId
                      ? 'Tracked by Material ID — use Add Stock to receive more, or Report Damaged/Scrap to remove units.'
                      : 'This replaces the site\'s current stock quantity for this item.')
                  : 'This quantity will be added to the site\'s existing stock.'}
              </p>
            </div>
            {!editStatInvId && addStatItem?.requiresMaterialId && (
              <div>
                <label className="label">
                  Material IDs <span className="text-gray-400 font-normal">(one per line — paste a column from Excel, or type manually; must match Quantity exactly)</span>
                </label>
                <textarea
                  className="input"
                  rows={5}
                  value={addStatStockMaterialIds}
                  onChange={(e) => setAddStatStockMaterialIds(e.target.value)}
                  placeholder={'MID-001\nMID-002\nMID-003'}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {addStatStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {addStatStockForm.quantity || 0} entered
                </p>
              </div>
            )}
            {addStatStockError && <p className="text-red-600 text-sm">{addStatStockError}</p>}
            <ModalActions
              onCancel={() => { setShowAddStatStock(false); setEditStatInvId(null); setAddStatStockError(''); }}
              submitLabel={editStatInvId ? 'Save' : 'Add Stock'}
            />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        open={showAddStatStockConfirm}
        title="Confirm Stock Addition"
        message="You do not have permission to edit this stock entry after it is added. Please verify all details carefully before proceeding. Do you want to continue?"
        danger={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={submitAddStatStock}
        onCancel={() => setShowAddStatStockConfirm(false)}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          IT ADD STOCK MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {showAddItStock && (
        <Modal
          title={editItInvId ? 'Edit Stock' : 'Add IT Stock'}
          subtitle={selectedSite ? `Site: ${selectedSite.name}` : undefined}
          onClose={() => { setShowAddItStock(false); setEditItInvId(null); setAddItStockError(''); setAddItStockMaterialIds(''); }}
        >
          <form onSubmit={handleAddItStockSubmit} className="space-y-4">
            <div>
              <label className="label">Item *</label>
              <SearchableSelect
                options={allItItems.filter((it) => it.category === itCategoryFilter).map((it) => ({ id: it.id, label: `${it.name} · ${it.unit}` }))}
                value={addItStockForm.itemId}
                onChange={(id) => setAddItStockForm({ ...addItStockForm, itemId: id })}
                placeholder="Search item…"
                disabled={!!editItInvId}
                required
              />
            </div>
            <div>
              <label className="label">{editItInvId ? 'Quantity *' : 'Quantity to Add *'}</label>
              <input
                className="input"
                type="number"
                min={editItInvId ? '0' : '1'}
                value={addItStockForm.quantity}
                onChange={(e) => setAddItStockForm({ ...addItStockForm, quantity: e.target.value })}
                placeholder="e.g. 50"
                required
                disabled={!!editItInvId && addItItem?.requiresMaterialId}
              />
              <p className="text-xs text-gray-400 mt-1">
                {editItInvId
                  ? (addItItem?.requiresMaterialId
                      ? 'Tracked by Material ID — use Add Stock to receive more, or Report Damaged/Scrap to remove units.'
                      : 'This replaces the site\'s current stock quantity for this item.')
                  : 'This quantity will be added to the site\'s existing stock.'}
              </p>
            </div>
            {!editItInvId && addItItem?.requiresMaterialId && (
              <div>
                <label className="label">
                  Material IDs <span className="text-gray-400 font-normal">(one per line — paste a column from Excel, or type manually; must match Quantity exactly)</span>
                </label>
                <textarea
                  className="input"
                  rows={5}
                  value={addItStockMaterialIds}
                  onChange={(e) => setAddItStockMaterialIds(e.target.value)}
                  placeholder={'MID-001\nMID-002\nMID-003'}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {addItStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {addItStockForm.quantity || 0} entered
                </p>
              </div>
            )}
            {addItStockError && <p className="text-red-600 text-sm">{addItStockError}</p>}
            <ModalActions
              onCancel={() => { setShowAddItStock(false); setEditItInvId(null); setAddItStockError(''); }}
              submitLabel={editItInvId ? 'Save' : 'Add Stock'}
            />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        open={showAddItStockConfirm}
        title="Confirm Stock Addition"
        message="You do not have permission to edit this stock entry after it is added. Please verify all details carefully before proceeding. Do you want to continue?"
        danger={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={submitAddItStock}
        onCancel={() => setShowAddItStockConfirm(false)}
      />

      {/* Manage Material IDs — assign IDs to already-existing untracked
          stock, or rename an existing code. Shared by Materials, Stationery & IT. */}
      {manageIdsTarget && (() => {
        // Shortfall must stay scoped to IN_STOCK units only — aggQty (from
        // StoreInventory/StationeryInventory) is itself an in-stock count,
        // and the backend's own assign-codes check compares against IN_STOCK
        // only (see materialUnits.js create()). manageIdsUnits now holds
        // every status (for browsing), so filter before comparing.
        const inStockCount = (manageIdsUnits || []).filter((u) => u.status === 'IN_STOCK').length;
        const shortfall = Math.max(0, (manageIdsTarget.aggQty || 0) - inStockCount);
        const newCodesCount = manageIdsNewCodes.split('\n').map((s) => s.trim()).filter(Boolean).length;
        const canManageIds = manageIdsTarget.kitId ? hasPermission('inventory_edit') : manageIdsTarget.itItemId ? hasPermission('it_edit') : hasPermission('stationery_edit');
        return (
          <Modal title="Material ID Units" subtitle={manageIdsTarget.name} onClose={() => setManageIdsTarget(null)}>
            {manageIdsUnits === null ? (
              <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Units ({manageIdsUnits.length})
                  </p>
                  {manageIdsUnits.length === 0 ? (
                    <p className="text-sm text-gray-400">No tracked units yet.</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                      {manageIdsUnits.map((u) => (
                        <div key={u.id} className="px-3 py-2 flex items-center gap-2">
                          <button
                            className="flex-1 font-mono text-sm text-left text-blue-600 hover:underline"
                            onClick={() => { const backTo = manageIdsTarget; setManageIdsTarget(null); openMaterialHistory(u.id); setDetailBackTarget(backTo); }}
                            title="View details and history — rename from there"
                          >
                            {u.materialIdCode}
                          </button>
                          <span className="text-xs text-gray-400">{u.status.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {canManageIds && shortfall > 0 && (
                  <div>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3">
                      <p className="text-xs text-amber-800 leading-relaxed">
                        {shortfall} unit{shortfall === 1 ? '' : 's'} of this stock at this site {shortfall === 1 ? 'has' : 'have'} no Material ID yet — assign one to each to make {shortfall === 1 ? 'it' : 'them'} issuable.
                      </p>
                    </div>
                    <label className="label">
                      Material IDs <span className="text-gray-400 font-normal">(one per line — assign as many as you have now, up to {shortfall}; the rest can be added later)</span>
                    </label>
                    <textarea
                      className="input"
                      rows={4}
                      value={manageIdsNewCodes}
                      onChange={(e) => setManageIdsNewCodes(e.target.value)}
                      placeholder={'MID-001\nMID-002'}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {newCodesCount} of {shortfall} entered
                      {newCodesCount > shortfall && <span className="text-red-600"> — exceeds the remaining shortfall</span>}
                    </p>
                    {manageIdsError && <p className="text-red-600 text-sm mt-2">{manageIdsError}</p>}
                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={manageIdsSaving || newCodesCount === 0 || newCodesCount > shortfall}
                        onClick={submitManageIdsAssign}
                      >
                        Assign IDs
                      </button>
                    </div>
                  </div>
                )}
                {!canManageIds && shortfall > 0 && (
                  <p className="text-xs text-gray-400">
                    {shortfall} unit{shortfall === 1 ? '' : 's'} still {shortfall === 1 ? 'needs' : 'need'} a Material ID assigned — ask someone with edit access.
                  </p>
                )}
                {shortfall === 0 && manageIdsError && <p className="text-red-600 text-sm">{manageIdsError}</p>}
              </div>
            )}
          </Modal>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════════
          STOCK MODALS
      ════════════════════════════════════════════════════════════════════════ */}

      {/* Kit Detail — CURRENTLY issued only (not returned), read-only. The
          number this opens from already only counts what's still out
          (quantity - returnedQuantity, see issuedByKit above), so the
          click-through should match that, not the full ever-issued history. */}
      {showKitDetail && kitDetailTarget && (() => {
        const filtered = kitDetailIssuances.filter((wk) => !wk.isReturned && inDateRange(wk, 'issuedDate', kitDetailFrom, kitDetailTo));
        const totalQty = filtered.reduce((s, wk) => s + (wk.quantity - (wk.returnedQuantity || 0)), 0);
        const filtering = !!(kitDetailFrom || kitDetailTo);
        return (
        <Modal
          title={kitDetailTarget.kit?.name}
          subtitle={kitDetailLoading ? 'Loading…' : `${totalQty} unit${totalQty !== 1 ? 's' : ''} currently issued across ${filtered.length} issuance${filtered.length !== 1 ? 's' : ''}${filtering ? ' in this period' : ''}`}
          onClose={() => { setShowKitDetail(false); setKitDetailTarget(null); }}
        >
          <DateRangeFilter from={kitDetailFrom} to={kitDetailTo} onFrom={setKitDetailFrom} onTo={setKitDetailTo} />
          <div className="space-y-2">
            {kitDetailLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">{filtering ? 'No issuances in this period' : 'Nothing currently issued'}</p>
            ) : filtered.map((wk) => {
              const outstanding = wk.quantity - (wk.returnedQuantity || 0);
              const workerName  = wk.worker?.name || workerMap[wk.workerId]?.name || '—';
              const workerEmpId = wk.worker?.employeeId || workerMap[wk.workerId]?.employeeId || '';
              return (
                <div key={wk.id} className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {workerName}
                        {!isWorkerOfThisSite(wk.workerId) && (
                          <span className="ml-1.5 text-xs font-normal text-amber-600">(not a worker of this site)</span>
                        )}
                      </p>
                      {workerEmpId && <p className="text-xs text-gray-500 font-mono">{workerEmpId}</p>}
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-white text-indigo-700 border-indigo-200">
                      {outstanding} out
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Issued {wk.quantity} on {new Date(wk.issuedDate).toLocaleDateString('en-GB')}
                    {wk.validityEndsAt && ` · Valid until ${new Date(wk.validityEndsAt).toLocaleDateString('en-GB')}`}
                  </p>
                  {wk.materialUnit && (
                    <p className="text-xs mt-1">
                      Material ID:{' '}
                      <button
                        className="font-mono text-indigo-600 hover:underline"
                        onClick={() => openMaterialHistory(wk.materialUnit.id)}
                      >
                        {wk.materialUnit.materialIdCode}
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
        );
      })()}

      {/* Stat (stationery) Detail — CURRENTLY issued only (not returned),
          read-only — same reasoning as the Kit Detail modal above. */}
      {showStatDetail && statDetailTarget && (() => {
        const filtered = statDetailIssuances.filter((iss) => !iss.isReturned && inDateRange(iss, 'issuedDate', statDetailFrom, statDetailTo));
        const totalQty = filtered.reduce((s, iss) => s + (iss.quantity - (iss.returnedQty || 0)), 0);
        const filtering = !!(statDetailFrom || statDetailTo);
        return (
        <Modal
          title={statDetailTarget.item?.name}
          subtitle={statDetailLoading ? 'Loading…' : `${totalQty} unit${totalQty !== 1 ? 's' : ''} currently issued across ${filtered.length} issuance${filtered.length !== 1 ? 's' : ''}${filtering ? ' in this period' : ''}`}
          onClose={() => { setShowStatDetail(false); setStatDetailTarget(null); }}
        >
          <DateRangeFilter from={statDetailFrom} to={statDetailTo} onFrom={setStatDetailFrom} onTo={setStatDetailTo} />
          <div className="space-y-2">
            {statDetailLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">{filtering ? 'No issuances in this period' : 'Nothing currently issued'}</p>
            ) : filtered.map((iss) => {
              const outstanding = iss.quantity - (iss.returnedQty || 0);
              const workerName  = iss.worker?.name || workerMap[iss.workerId]?.name || '—';
              const workerEmpId = iss.worker?.employeeId || workerMap[iss.workerId]?.employeeId || '';
              return (
                <div key={iss.id} className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {workerName}
                        {!isWorkerOfThisSite(iss.workerId) && (
                          <span className="ml-1.5 text-xs font-normal text-amber-600">(not a worker of this site)</span>
                        )}
                      </p>
                      {workerEmpId && <p className="text-xs text-gray-500 font-mono">{workerEmpId}</p>}
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-white text-indigo-700 border-indigo-200">
                      {outstanding} out
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Issued {iss.quantity} on {new Date(iss.issuedDate).toLocaleDateString('en-GB')}
                    {iss.validityEndsAt && ` · Valid until ${new Date(iss.validityEndsAt).toLocaleDateString('en-GB')}`}
                  </p>
                  {iss.materialUnit && (
                    <p className="text-xs mt-1">
                      Material ID:{' '}
                      <button
                        className="font-mono text-indigo-600 hover:underline"
                        onClick={() => openMaterialHistory(iss.materialUnit.id)}
                      >
                        {iss.materialUnit.materialIdCode}
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
        );
      })()}

      {/* IT Detail — same idea as Stat Detail, for an IT item */}
      {showItDetail && itDetailTarget && (() => {
        const filtered = itDetailIssuances.filter((iss) => !iss.isReturned && inDateRange(iss, 'issuedDate', itDetailFrom, itDetailTo));
        const totalQty = filtered.reduce((s, iss) => s + (iss.quantity - (iss.returnedQty || 0)), 0);
        const filtering = !!(itDetailFrom || itDetailTo);
        return (
        <Modal
          title={itDetailTarget.item?.name}
          subtitle={itDetailLoading ? 'Loading…' : `${totalQty} unit${totalQty !== 1 ? 's' : ''} currently issued across ${filtered.length} issuance${filtered.length !== 1 ? 's' : ''}${filtering ? ' in this period' : ''}`}
          onClose={() => { setShowItDetail(false); setItDetailTarget(null); }}
        >
          <DateRangeFilter from={itDetailFrom} to={itDetailTo} onFrom={setItDetailFrom} onTo={setItDetailTo} />
          <div className="space-y-2">
            {itDetailLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">{filtering ? 'No issuances in this period' : 'Nothing currently issued'}</p>
            ) : filtered.map((iss) => {
              const outstanding = iss.quantity - (iss.returnedQty || 0);
              const workerName  = iss.worker?.name || workerMap[iss.workerId]?.name || '—';
              const workerEmpId = iss.worker?.employeeId || workerMap[iss.workerId]?.employeeId || '';
              return (
                <div key={iss.id} className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {workerName}
                        {!isWorkerOfThisSite(iss.workerId) && (
                          <span className="ml-1.5 text-xs font-normal text-amber-600">(not a worker of this site)</span>
                        )}
                      </p>
                      {workerEmpId && <p className="text-xs text-gray-500 font-mono">{workerEmpId}</p>}
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-white text-indigo-700 border-indigo-200">
                      {outstanding} out
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Issued {iss.quantity} on {new Date(iss.issuedDate).toLocaleDateString('en-GB')}
                    {iss.validityEndsAt && ` · Valid until ${new Date(iss.validityEndsAt).toLocaleDateString('en-GB')}`}
                  </p>
                  {iss.materialUnit && (
                    <p className="text-xs mt-1">
                      Material ID:{' '}
                      <button
                        className="font-mono text-indigo-600 hover:underline"
                        onClick={() => openMaterialHistory(iss.materialUnit.id)}
                      >
                        {iss.materialUnit.materialIdCode}
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
        );
      })()}

      {/* Stock History — how "Stock Available" got here (purchases + transfers-in) */}
      {showStockHistory && stockHistoryTarget && (() => {
        const filtered = stockHistoryRows.filter((row) => inDateRange(row, 'date', stockHistoryFrom, stockHistoryTo));
        // Edits are corrections, not new stock received — excluded from the
        // "added" total so a quantity/date/remarks fix doesn't inflate it.
        const addedRows = filtered.filter((row) => row.type !== 'EDITED');
        const totalQty = addedRows.reduce((s, row) => s + row.quantity, 0);
        const filtering = !!(stockHistoryFrom || stockHistoryTo);
        return (
        <Modal
          title={stockHistoryTarget.name}
          subtitle={stockHistoryLoading ? 'Loading…' : `${totalQty} unit${totalQty !== 1 ? 's' : ''} added across ${addedRows.length} record${addedRows.length !== 1 ? 's' : ''}${filtering ? ' in this period' : ''}`}
          onClose={() => { setShowStockHistory(false); setStockHistoryTarget(null); }}
        >
          <DateRangeFilter from={stockHistoryFrom} to={stockHistoryTo} onFrom={setStockHistoryFrom} onTo={setStockHistoryTo} />
          <div className="space-y-2">
            {stockHistoryLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">{filtering ? 'No stock records in this period' : 'No stock records yet'}</p>
            ) : filtered.map((row, i) => (
              <div key={i} className="p-3 rounded-lg border border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">
                    {row.type === 'EDITED' ? 'Stock Entry Edited'
                      : row.type === 'TRANSFERRED_IN' ? `Transferred from ${row.fromSite || 'another site'}`
                      : 'Stock Added'}
                  </p>
                  {row.type === 'EDITED' ? (
                    <span className="text-xs font-semibold bg-white text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                      {row.previousQuantity} → {row.quantity}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold bg-white text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                      +{row.quantity}{row.beforeQty != null && row.afterQty != null ? ` (${row.beforeQty} → ${row.afterQty})` : ''}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(row.date).toLocaleDateString('en-GB')}
                  {row.by ? ` · by ${row.by}` : ''}
                  {row.totalValue != null ? ` · ₹${parseFloat(row.totalValue).toFixed(0)}` : ''}
                  {row.type === 'EDITED' && row.remarks ? ` · "${row.remarks}"` : ''}
                </p>
              </div>
            ))}
          </div>
        </Modal>
        );
      })()}

      {/* Add / Edit Stock */}
      {showAddStock && (
        <Modal title={editInvId ? 'Edit Stock' : 'Add Stock'} onClose={() => { setShowAddStock(false); setEditInvId(null); setError(''); }}>
          <form onSubmit={handleAddStockSubmit} className="space-y-4">
            <div>
              <label className="label">Material</label>
              <SearchableSelect
                options={allKits.filter((k) => k.isActive).map((k) => ({ id: k.id, label: `${k.name} (${materialTypeLabel(k.type)})` }))}
                value={addStockForm.kitId}
                onChange={(id) => setAddStockForm({ ...addStockForm, kitId: id })}
                placeholder="Search material…"
                disabled={!!editInvId}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Quantity</label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={addStockForm.quantity}
                  onChange={(e) => setAddStockForm({ ...addStockForm, quantity: e.target.value })}
                  required
                  disabled={!!editInvId && addStockKit?.requiresMaterialId}
                />
                {editInvId && addStockKit?.requiresMaterialId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Tracked by Material ID — use Add Stock to receive more, or Report Damaged/Scrap to remove units.
                  </p>
                )}
              </div>
              <div>
                <label className="label">Received Date</label>
                <input className="input" type="date" value={addStockForm.receivedDate} onChange={(e) => setAddStockForm({ ...addStockForm, receivedDate: e.target.value })} required />
              </div>
            </div>
            <div>
              <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
              <input className="input" value={addStockForm.remarks} onChange={(e) => setAddStockForm({ ...addStockForm, remarks: e.target.value })} />
            </div>
            {!editInvId && addStockKit?.requiresMaterialId && (
              <div>
                <label className="label">
                  Material IDs <span className="text-gray-400 font-normal">(one per line — paste a column from Excel, or type manually; must match Quantity exactly)</span>
                </label>
                <textarea
                  className="input"
                  rows={5}
                  value={addStockMaterialIds}
                  onChange={(e) => setAddStockMaterialIds(e.target.value)}
                  placeholder={'MID-001\nMID-002\nMID-003'}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {addStockMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {addStockForm.quantity || 0} entered
                </p>
              </div>
            )}
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <ModalActions onCancel={() => { setShowAddStock(false); setEditInvId(null); setError(''); }} submitLabel="Save" />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        open={showAddStockConfirm}
        title="Confirm Stock Addition"
        message="You do not have permission to edit this stock entry after it is added. Please verify all details carefully before proceeding. Do you want to continue?"
        danger={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={submitAddStock}
        onCancel={() => setShowAddStockConfirm(false)}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          ISSUE MODAL  (z-[60] — above any other modals)
      ════════════════════════════════════════════════════════════════════════ */}
      {issueWorker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Issue to {issueWorker.name}</h3>
                <p className="text-sm text-gray-500 font-mono mt-0.5">{issueWorker.employeeId}</p>
                {selectedSite && <p className="text-xs text-indigo-600 mt-0.5">Site: {selectedSite.name}</p>}
              </div>
              <button onClick={() => setIssueWorker(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>

            {/* Type toggle */}
            <div className="flex gap-0 mb-5 p-1 bg-gray-100 rounded-lg">
              {[
                ['material', 'Materials'], ['stationery', 'Stationery Item'],
                // IT — only offered when the acting user holds both it_view
                // (can see IT stock at all) and it_issue (can actually issue it).
                ...(canViewIT && hasPermission('it_issue') ? [['it', 'IT Item']] : []),
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                    issueType === v ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setIssueType(v)}
                >
                  {l}
                </button>
              ))}
            </div>

            {issueType === 'material' ? (
              /* ── Material Issue ──────────────────────────────────────────── */
              <form onSubmit={handleMatIssueSubmit} className="space-y-4">
                <div>
                  <label className="label">Type</label>
                  <select
                    className="input"
                    value={issueMatType}
                    onChange={(e) => { setIssueMatType(e.target.value); setSelectedInv(null); setValidityWarn(null); setConfirmDeduction(false); setMaxQtyWarn(null); setConfirmMaxQtyDeduction(false); }}
                  >
                    <option value="ALL">All Types</option>
                    {MATERIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Material</label>
                  {availableMatInvForIssue.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                      No materials {issueMatType === 'ALL' ? '' : 'of this type '}in stock at this site.
                    </div>
                  ) : (
                    <select
                      className="input"
                      value={selectedInv ? String(selectedInv.id) : ''}
                      onChange={(e) => handleInvSelect(e.target.value)}
                      required
                    >
                      <option value="">Select material…</option>
                      {availableMatInvForIssue.map((b) => {
                        const avail = (b.quantity || 0) - (b.damagedQuantity || 0);
                        return (
                          <option key={b.id} value={b.id}>
                            {b.kit?.name} · Available: {avail}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>

                {selectedInv && (
                  <>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Available</span>
                        <span className="font-semibold text-green-700">{availableForIssue} {selectedInv.kit?.unit}</span>
                      </div>
                      {selectedInv.kit?.validityDays && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Validity</span>
                          <span className="text-gray-700">{selectedInv.kit.validityDays} days from issue date</span>
                        </div>
                      )}
                      {selectedInv.kit?.price && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Price</span>
                          <span className="text-gray-700">₹{parseFloat(selectedInv.kit.price).toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {validityWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Item Still Within Validity Period</p>
                        <p className="text-amber-700">{validityWarn.message}</p>
                        {validityWarn.validityEndsAt && (
                          <p className="text-amber-600 text-xs mt-1">
                            Current validity ends: {new Date(validityWarn.validityEndsAt).toLocaleDateString('en-GB')}
                          </p>
                        )}
                        {validityWarn.price && (
                          <p className="text-amber-700 mt-2 font-medium">
                            ₹{validityWarn.price} will be deducted from this worker's salary.
                          </p>
                        )}
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmDeduction}
                            onChange={(e) => setConfirmDeduction(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    {maxQtyWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Maximum Quantity Reached</p>
                        <p className="text-amber-700">{maxQtyWarn.message}</p>
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmMaxQtyDeduction}
                            onChange={(e) => setConfirmMaxQtyDeduction(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    <div>
                      <label className="label">Quantity</label>
                      <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        1 {selectedInv.kit?.unit} — safety kit materials are individually tracked and can only be issued one at a time.
                      </p>
                    </div>
                    {selectedInv.kit?.requiresMaterialId && (
                      <MaterialIdPicker
                        value={selectedMaterialUnitId}
                        onChange={setSelectedMaterialUnitId}
                        units={availableMaterialUnits}
                        kitId={selectedInv.kitId}
                        siteId={selectedInv.siteId}
                        onUnitAdded={(unit) => setAvailableMaterialUnits((prev) => [...prev, unit])}
                      />
                    )}
                    <div>
                      <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input className="input" value={matRemarks} onChange={(e) => setMatRemarks(e.target.value)} />
                    </div>
                  </>
                )}

                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button type="button" className="btn-secondary" onClick={() => setIssueWorker(null)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!selectedInv || availableMatInvForIssue.length === 0 || (!!validityWarn && !confirmDeduction) || (!!maxQtyWarn && !confirmMaxQtyDeduction) || (!!selectedInv?.kit?.requiresMaterialId && !selectedMaterialUnitId)}
                  >
                    Issue Material
                  </button>
                </div>
              </form>
            ) : issueType === 'it' ? (
              /* ── IT Issue ─────────────────────────────────────────────────── */
              <form onSubmit={handleItIssueSubmit} className="space-y-4">
                {/* Item */}
                <div>
                  <label className="label">Item</label>
                  <select
                    className="input"
                    value={itIssueItemId}
                    onChange={(e) => handleItItemSelect(e.target.value, itItemsForIssue)}
                    required
                  >
                    <option value="">Select item…</option>
                    {itItemsForIssue.map((iinv) => (
                      <option key={iinv.itemId} value={iinv.itemId}>
                        {iinv.item?.name} (Available: {iinv.quantity})
                      </option>
                    ))}
                  </select>
                  {itItemsForIssue.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No IT items found.
                    </p>
                  )}
                </div>

                {itIssueItemId && (
                  <>
                    {selectedItItem && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                        {selectedItItem.price && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Price</span>
                            <span className="font-medium">₹{parseFloat(selectedItItem.price).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">In stock</span>
                          <span className="font-semibold text-green-700">{itItemStock}</span>
                        </div>
                      </div>
                    )}

                    {itIssueWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Unreturned Item</p>
                        <p className="text-amber-700">{itIssueWarn.message}</p>
                        {itIssueWarn.price && (
                          <p className="text-amber-700 mt-2 font-medium">
                            ₹{itIssueWarn.price} will be deducted from this worker's salary.
                          </p>
                        )}
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={itConfirmDed}
                            onChange={(e) => setItConfirmDed(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    {itMaxQtyWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Maximum Quantity Reached</p>
                        <p className="text-amber-700">{itMaxQtyWarn.message}</p>
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={itConfirmMaxQtyDed}
                            onChange={(e) => setItConfirmMaxQtyDed(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    {selectedItItem?.requiresMaterialId && (
                      <MaterialIdPicker
                        value={selectedItMaterialUnitId}
                        onChange={setSelectedItMaterialUnitId}
                        units={availableItMaterialUnits}
                        itItemId={selectedItInv.itemId}
                        siteId={selectedSiteId}
                        onUnitAdded={(unit) => setAvailableItMaterialUnits((prev) => [...prev, unit])}
                      />
                    )}

                    <div>
                      <label className="label">Quantity</label>
                      {itCategory === 'DURABLE' ? (
                        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          1 {selectedItItem?.unit || ''} — durable items are individually tracked and can only be issued one at a time.
                        </p>
                      ) : (
                        <input
                          className="input"
                          type="number"
                          min="1"
                          max={itItemStock || undefined}
                          value={itIssueQty}
                          onChange={(e) => setItIssueQty(e.target.value)}
                          required
                        />
                      )}
                    </div>
                    <div>
                      <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input className="input" value={itIssueRemarks} onChange={(e) => setItIssueRemarks(e.target.value)} />
                    </div>
                  </>
                )}

                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button type="button" className="btn-secondary" onClick={() => setIssueWorker(null)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!itIssueItemId || itItemsForIssue.length === 0 || (!!itIssueWarn && !itConfirmDed) || (!!itMaxQtyWarn && !itConfirmMaxQtyDed) || (!!selectedItItem?.requiresMaterialId && !selectedItMaterialUnitId)}
                  >
                    Issue IT Item
                  </button>
                </div>
              </form>
            ) : (
              /* ── Stationery Issue ────────────────────────────────────────── */
              <form onSubmit={handleStatIssueSubmit} className="space-y-4">
                {/* Category */}
                <div className="flex gap-1">
                  {[['CONSUMABLE', 'Consumable'], ['DURABLE', 'Durable']].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      className={statCategory === v ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
                      onClick={() => { setStatCategory(v); setStatIssueItemId(''); setStatIssueQty(1); setStatIssueWarn(null); setStatConfirmDed(false); setStatMaxQtyWarn(null); setStatConfirmMaxQtyDed(false); }}
                    >
                      {l}
                    </button>
                  ))}
                </div>

                {/* Item */}
                <div>
                  <label className="label">Item</label>
                  <select
                    className="input"
                    value={statIssueItemId}
                    onChange={(e) => handleStatItemSelect(e.target.value, statItemsForIssue)}
                    required
                  >
                    <option value="">Select item…</option>
                    {statItemsForIssue.map((sinv) => (
                      <option key={sinv.itemId} value={sinv.itemId}>
                        {sinv.item?.name} (Available: {sinv.quantity})
                      </option>
                    ))}
                  </select>
                  {statItemsForIssue.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No {statCategory === 'CONSUMABLE' ? 'consumable' : 'durable'} items found.
                    </p>
                  )}
                </div>

                {statIssueItemId && (
                  <>
                    {selectedStatItem && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                        {selectedStatItem.price && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Price</span>
                            <span className="font-medium">₹{parseFloat(selectedStatItem.price).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">In stock</span>
                          <span className="font-semibold text-green-700">{statItemStock}</span>
                        </div>
                      </div>
                    )}

                    {statIssueWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Unreturned Item</p>
                        <p className="text-amber-700">{statIssueWarn.message}</p>
                        {statIssueWarn.price && (
                          <p className="text-amber-700 mt-2 font-medium">
                            ₹{statIssueWarn.price} will be deducted from this worker's salary.
                          </p>
                        )}
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={statConfirmDed}
                            onChange={(e) => setStatConfirmDed(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    {statMaxQtyWarn && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-amber-800 mb-1">⚠ Maximum Quantity Reached</p>
                        <p className="text-amber-700">{statMaxQtyWarn.message}</p>
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={statConfirmMaxQtyDed}
                            onChange={(e) => setStatConfirmMaxQtyDed(e.target.checked)}
                            className="w-4 h-4 accent-amber-600"
                          />
                          <span className="text-amber-800 font-medium text-sm">Confirm salary deduction and proceed</span>
                        </label>
                      </div>
                    )}

                    {selectedStatItem?.requiresMaterialId && (
                      <MaterialIdPicker
                        value={selectedStatMaterialUnitId}
                        onChange={setSelectedStatMaterialUnitId}
                        units={availableStatMaterialUnits}
                        stationeryItemId={selectedStatInv.itemId}
                        siteId={selectedSiteId}
                        onUnitAdded={(unit) => setAvailableStatMaterialUnits((prev) => [...prev, unit])}
                      />
                    )}

                    <div>
                      <label className="label">Quantity</label>
                      {statCategory === 'DURABLE' ? (
                        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          1 {selectedStatItem?.unit || ''} — durable items are individually tracked and can only be issued one at a time.
                        </p>
                      ) : (
                        <input
                          className="input"
                          type="number"
                          min="1"
                          max={statItemStock || undefined}
                          value={statIssueQty}
                          onChange={(e) => setStatIssueQty(e.target.value)}
                          required
                        />
                      )}
                    </div>
                    <div>
                      <label className="label">Remarks <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input className="input" value={statIssueRemarks} onChange={(e) => setStatIssueRemarks(e.target.value)} />
                    </div>
                  </>
                )}

                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button type="button" className="btn-secondary" onClick={() => setIssueWorker(null)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!statIssueItemId || statItemsForIssue.length === 0 || (!!statIssueWarn && !statConfirmDed) || (!!statMaxQtyWarn && !statConfirmMaxQtyDed) || (!!selectedStatItem?.requiresMaterialId && !selectedStatMaterialUnitId)}
                  >
                    Issue {statCategory === 'CONSUMABLE' ? 'Consumable' : 'Durable'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          RETURN MODALS  (z-[70] — above issue modal)
      ════════════════════════════════════════════════════════════════════════ */}

      {/* Return chooser — Normal Return vs Damaged Return */}
      {returnChoiceTarget && (
        <Modal
          title="Return Item"
          subtitle={returnChoiceTarget.kind === 'MATERIAL' ? returnChoiceTarget.target.kit?.name : returnChoiceTarget.target.item?.name}
          onClose={() => setReturnChoiceTarget(null)}
        >
          <div className="space-y-3">
            <button
              className="btn-primary w-full"
              onClick={() => {
                const { kind, target, outstanding } = returnChoiceTarget;
                if (kind === 'MATERIAL') {
                  setMatReturnTarget(target);
                  setMatReturnForm({ returnedQuantity: outstanding });
                } else if (kind === 'IT') {
                  setItReturnTarget(target);
                  setItReturnForm({ returnedQty: target.quantity });
                } else {
                  setStatReturnTarget(target);
                  setStatReturnForm({ returnedQty: target.quantity });
                }
                setReturnChoiceTarget(null);
                setError('');
              }}
            >
              Normal Return
            </button>
            {/* Damaged Return is Materials-only — Stationery Durables use the
                separate Repair & Maintenance workflow ("Mark as Needs Repair")
                instead of Damage Approval. */}
            {returnChoiceTarget.kind === 'MATERIAL' && hasPermission('damage_flagging') && (
              <button
                className="w-full text-white bg-red-600 hover:bg-red-700 font-medium px-4 py-2 rounded-lg transition-colors"
                onClick={() => {
                  openFlagDamaged({ kind: returnChoiceTarget.kind, target: returnChoiceTarget.target });
                  setReturnChoiceTarget(null);
                }}
              >
                Damaged Return
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* Material Return */}
      {matReturnTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Return Material</h3>
            <p className="text-sm text-gray-500 mb-4">{matReturnTarget.kit?.name}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
              Issued: <strong>{matReturnTarget.quantity}</strong> ·{' '}
              Already returned: <strong>{matReturnTarget.returnedQuantity || 0}</strong> ·{' '}
              Outstanding: <strong>{matReturnTarget.quantity - (matReturnTarget.returnedQuantity || 0)}</strong>
            </div>
            <form onSubmit={handleMatReturn} className="space-y-4">
              <div>
                <label className="label">Quantity Being Returned</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max={matReturnTarget.quantity - (matReturnTarget.returnedQuantity || 0)}
                  value={matReturnForm.returnedQuantity}
                  onChange={(e) => setMatReturnForm({ ...matReturnForm, returnedQuantity: e.target.value })}
                  required
                />
              </div>
              {!matReturnTarget.materialUnit && matReturnTarget.kit?.requiresMaterialId && (
                <div>
                  <label className="label">
                    Material IDs <span className="text-gray-400 font-normal">(one per line — this material now requires one per unit, and this issuance predates that)</span>
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    value={matReturnMaterialIds}
                    onChange={(e) => setMatReturnMaterialIds(e.target.value)}
                    placeholder={'MID-001'}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {matReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {matReturnForm.returnedQuantity || 0} entered
                  </p>
                </div>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" className="btn-secondary" onClick={() => { setMatReturnTarget(null); setError(''); setMatReturnMaterialIds(''); }}>Cancel</button>
                <button type="submit" className="btn-primary">Confirm Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stationery Return */}
      {statReturnTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Return Stationery Item</h3>
            <p className="text-sm text-gray-500 mb-4">{statReturnTarget.item?.name}</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm text-gray-700">
              Issued qty: <strong>{statReturnTarget.quantity}</strong>
            </div>
            <form onSubmit={handleStatReturn} className="space-y-4">
              <div>
                <label className="label">Quantity Returned</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max={statReturnTarget.quantity}
                  value={statReturnForm.returnedQty}
                  onChange={(e) => setStatReturnForm({ ...statReturnForm, returnedQty: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
              {!statReturnTarget.materialUnit && statReturnTarget.item?.requiresMaterialId && (
                <div>
                  <label className="label">
                    Material IDs <span className="text-gray-400 font-normal">(one per line — this item now requires one per unit, and this issuance predates that)</span>
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    value={statReturnMaterialIds}
                    onChange={(e) => setStatReturnMaterialIds(e.target.value)}
                    placeholder={'MID-001'}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {statReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {statReturnForm.returnedQty || 0} entered
                  </p>
                </div>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" className="btn-secondary" onClick={() => { setStatReturnTarget(null); setError(''); setStatReturnMaterialIds(''); }}>Cancel</button>
                <button type="submit" className="btn-primary">Confirm Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* IT Return */}
      {itReturnTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Return IT Item</h3>
            <p className="text-sm text-gray-500 mb-4">{itReturnTarget.item?.name}</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm text-gray-700">
              Issued qty: <strong>{itReturnTarget.quantity}</strong>
            </div>
            <form onSubmit={handleItReturn} className="space-y-4">
              <div>
                <label className="label">Quantity Returned</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  max={itReturnTarget.quantity}
                  value={itReturnForm.returnedQty}
                  onChange={(e) => setItReturnForm({ ...itReturnForm, returnedQty: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
              {!itReturnTarget.materialUnit && itReturnTarget.item?.requiresMaterialId && (
                <div>
                  <label className="label">
                    Material IDs <span className="text-gray-400 font-normal">(one per line — this item now requires one per unit, and this issuance predates that)</span>
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    value={itReturnMaterialIds}
                    onChange={(e) => setItReturnMaterialIds(e.target.value)}
                    placeholder={'MID-001'}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {itReturnMaterialIds.split('\n').map((s) => s.trim()).filter(Boolean).length} of {itReturnForm.returnedQty || 0} entered
                  </p>
                </div>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" className="btn-secondary" onClick={() => { setItReturnTarget(null); setError(''); setItReturnMaterialIds(''); }}>Cancel</button>
                <button type="submit" className="btn-primary">Confirm Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Bulk Stock Upload — Confirm before applying ───────────────────────── */}
      <ConfirmDialog
        open={!!bulkStockPending}
        danger={false}
        title="Confirm Bulk Stock Update"
        message={bulkStockPending ? `${bulkStockPending.willUpdate} ${bulkStockPending.kind === 'materials' ? 'material(s)' : 'stationery item(s)'} will be updated based on the Add Stock values in this file. This cannot be undone.` : ''}
        warning={bulkStockPending && (bulkStockPending.skipped > 0 || bulkStockPending.failed > 0)
          ? `${bulkStockPending.skipped} row(s) will be skipped (no stock added) and ${bulkStockPending.failed} row(s) failed validation — see the results after confirming for details.`
          : undefined}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={confirmBulkStockUpload}
        onCancel={() => setBulkStockPending(null)}
      />

      {/* ── Bulk Stock Upload Results ─────────────────────────────────────────── */}
      {bulkStockResult && (
        <Modal title="Bulk Stock Upload Results" onClose={() => setBulkStockResult(null)}>
          <div className="space-y-3">
            <div className={`rounded-lg border px-4 py-3 flex items-center gap-2 ${bulkStockResult.updated > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              {bulkStockResult.updated > 0 ? (
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className={`font-semibold text-base ${bulkStockResult.updated > 0 ? 'text-green-800' : 'text-gray-600'}`}>
                {bulkStockResult.updated} {bulkStockResult.kind === 'materials' ? 'material(s)' : 'stationery item(s)'} updated successfully
              </span>
            </div>
            <div className="flex gap-4 text-sm flex-wrap">
              <span className="text-gray-500 font-medium">{bulkStockResult.skipped || 0} skipped (no stock added)</span>
              <span className="text-red-600 font-medium">{bulkStockResult.failed} failed</span>
              <span className="text-gray-400">{bulkStockResult.total} total row{bulkStockResult.total !== 1 ? 's' : ''}</span>
            </div>
            {bulkStockResult.failedRecords?.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Row</th>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-left px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkStockResult.failedRecords.map((f, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">{f.row}</td>
                        <td className="px-3 py-1.5">{f.identifier}</td>
                        <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
            <button className="btn-primary" onClick={() => setBulkStockResult(null)}>Close</button>
          </div>
        </Modal>
      )}

      {bulkStockError && !bulkStockResult && (
        <Modal title="Bulk Stock Upload" onClose={() => setBulkStockError('')}>
          <p className="text-red-600 text-sm">{bulkStockError}</p>
          <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
            <button className="btn-primary" onClick={() => setBulkStockError('')}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
